import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import { getColors, taskCategories, weightOptions, getModalStyle } from '../theme';
import { UpgradePrompt, UpgradeLimitModal } from '../components/LockedFeature';
import { EventsPageSkeleton } from '../components/LoadingSkeleton';
import { devLog, devError, devWarn } from '../utils/debugLogger';
import { downloadICalEvent } from '../services/icalExport';
import {
  Plus,
  X,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Calendar as CalendarIcon,
  MapPin,
  Flag,
  Download,
  CheckSquare,
  Target,
  Check,
  RefreshCw,
  List,
  Grid,
  MoreVertical,
  Palette,
  AlertTriangle,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import {
  CALENDAR_COLORS,
  saveGoogleCalendarColors,
  getGoogleCalendarColor,
  setGoogleCalendarCustomColor,
  setGoogleCalendarVisibility,
  getEventColor,
  getTaskSettings as getSavedTaskSettings,
  getGoalSettings as getSavedGoalSettings,
} from '../config/calendarColors';

// SMART goal framework
const SMART_CRITERIA = [
  { key: 'specific', label: 'Specific', question: 'What exactly will you accomplish?' },
  { key: 'measurable', label: 'Measurable', question: 'How will you know when it is accomplished?' },
  { key: 'achievable', label: 'Achievable', question: 'Is this goal realistic given your resources?' },
  { key: 'relevant', label: 'Relevant', question: 'Why does this goal matter to you?' },
  { key: 'timeBound', label: 'Time-bound', question: 'When will you accomplish this goal?' }
];

const GOAL_TIMEFRAMES = [
  { value: 'monthly', label: 'Monthly', icon: CalendarIcon, color: '#6b675f' },
  { value: 'annual', label: 'Annual', icon: Flag, color: '#8a8a8a' }
];

// Persistent cache for recently synced events (survives page refresh)
const getSyncedEventCache = () => {
  try {
    const cached = localStorage.getItem('mind_synced_events_map');
    return cached ? JSON.parse(cached) : {};
  } catch (e) {
    return {};
  }
};

const setSyncedEventCache = (map) => {
  try {
    localStorage.setItem('mind_synced_events_map', JSON.stringify(map));
  } catch (e) {
    devWarn('[EventCache] Failed to persist sync cache:', e);
  }
};

// Helper to match events by name + date + time (fallback deduplication)
// Must include time to distinguish between different events with the same name
// For all-day events, normalize time to null to handle empty strings
const eventSignature = (event) => {
  const date = event.startDate || event.date;
  // For all-day events, startTime might be empty string, null, or undefined
  // Normalize all to null for comparison
  const time = (event.startTime && event.startTime.trim()) ? event.startTime : null;
  return `${(event.name || '').toLowerCase().trim()}|${date}|${time}`;
};

// Helper to render description with clickable links
// Handles both HTML <a> tags and plain URLs
const renderDescription = (description, colors, linkColor) => {
  if (!description) return null;
  
  const actualLinkColor = linkColor || colors.accent;
  
  // First, extract and replace HTML anchor tags
  const parts = [];
  let remaining = description;
  let key = 0;
  
  // Match <a href="...">...</a> tags
  const anchorRegex = /<a\s+(?:[^>]*?\s+)?href=["']([^"']*)["'][^>]*>([^<]*)<\/a>/gi;
  let match;
  let lastIndex = 0;
  
  while ((match = anchorRegex.exec(description)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      const textBefore = description.slice(lastIndex, match.index);
      parts.push(...renderPlainTextWithUrls(textBefore, key, colors, actualLinkColor));
      key += 10;
    }
    
    // Add the link
    const href = match[1];
    const linkText = match[2];
    parts.push(
      <a 
        key={key++} 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="underline hover:opacity-80"
        style={{ color: actualLinkColor }}
        onClick={(e) => e.stopPropagation()}
      >
        {linkText}
      </a>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < description.length) {
    const textAfter = description.slice(lastIndex);
    parts.push(...renderPlainTextWithUrls(textAfter, key, colors, actualLinkColor));
  }
  
  return parts.length > 0 ? parts : description;
};

// Helper to find and linkify plain URLs in text
const renderPlainTextWithUrls = (text, startKey, colors, linkColor) => {
  const urlRegex = /(https?:\/\/[^\s<]+)/gi;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = startKey;
  const actualLinkColor = linkColor || colors.accent;
  
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
    }
    parts.push(
      <a 
        key={key++} 
        href={match[1]} 
        target="_blank" 
        rel="noopener noreferrer"
        className="underline hover:opacity-80"
        style={{ color: actualLinkColor }}
        onClick={(e) => e.stopPropagation()}
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
  }
  
  return parts.length > 0 ? parts : [<span key={startKey}>{text}</span>];
};

// Constants for Linear-style event card spacing
const EVENT_GAP_PX = 2; // 2px horizontal gap between overlapping events
const EVENT_PADDING_H = 6; // 6px horizontal internal padding (reduced)
const EVENT_PADDING_V = 4; // 4px vertical internal padding (reduced)
const OVERLAP_INDENT_PX = 40; // Indent for stacked overlapping events (Google-style)

// Helper to calculate two-column layout for day view (side-by-side overlapping events)
const calculateTwoColumnLayout = (events) => {
  if (!events || events.length === 0) return [];

  // 1. Prepare events with start/end in minutes
  const processedEvents = events.map(e => {
    const [startH, startM] = (e.startTime || '08:00').split(':').map(Number);
    const [endH, endM] = (e.endTime || `${startH + 1}:00`).split(':').map(Number);
    return {
      ...e,
      startMin: startH * 60 + startM,
      endMin: endH * 60 + endM,
    };
  }).sort((a, b) => a.startMin - b.startMin || (b.endMin - a.endMin));

  // 2. Assign columns using a greedy algorithm
  // Each event gets the first available column
  const columns = []; // Array of arrays, each inner array is events in that column

  processedEvents.forEach(event => {
    // Find first column where this event doesn't overlap with any existing event
    let placed = false;
    for (let col = 0; col < columns.length; col++) {
      const columnEvents = columns[col];
      const overlaps = columnEvents.some(e => 
        event.startMin < e.endMin && event.endMin > e.startMin
      );
      if (!overlaps) {
        columns[col].push(event);
        event.column = col;
        placed = true;
        break;
      }
    }
    if (!placed) {
      // Need a new column
      event.column = columns.length;
      columns.push([event]);
    }
  });

  // 3. Calculate width and left position for each event
  const totalColumns = columns.length;
  processedEvents.forEach(event => {
    event.totalColumns = totalColumns;
    event.widthPercent = 100 / totalColumns;
    event.leftPercent = (event.column / totalColumns) * 100;
  });

  return processedEvents;
};

// Helper to calculate layout for overlapping events (week view)
// Uses Google-style stacking: first event full width, subsequent events indented
const calculateEventLayout = (events) => {
  if (!events || events.length === 0) return [];

  // 1. Prepare events with start/end in minutes
  const processedEvents = events.map(e => {
    const [startH, startM] = (e.startTime || '08:00').split(':').map(Number);
    const [endH, endM] = (e.endTime || `${startH + 1}:00`).split(':').map(Number);
    return {
      ...e,
      startMin: startH * 60 + startM,
      endMin: endH * 60 + endM,
    };
  }).sort((a, b) => a.startMin - b.startMin || (b.endMin - a.endMin));

  // 2. Group overlapping events into clusters
  const clusters = [];
  let currentCluster = [];
  let clusterEnd = -1;

  processedEvents.forEach(event => {
    if (currentCluster.length === 0) {
      currentCluster.push(event);
      clusterEnd = event.endMin;
    } else {
      if (event.startMin < clusterEnd) {
        currentCluster.push(event);
        clusterEnd = Math.max(clusterEnd, event.endMin);
      } else {
        clusters.push(currentCluster);
        currentCluster = [event];
        clusterEnd = event.endMin;
      }
    }
  });
  if (currentCluster.length > 0) clusters.push(currentCluster);

  // 3. Process each cluster with Google-style stacking layout
  // Each overlapping event gets indented but maintains readable width
  const finalEvents = [];
  
  clusters.forEach(cluster => {
    // Sort cluster by start time, then by duration (longer events first)
    cluster.sort((a, b) => a.startMin - b.startMin || (b.endMin - b.startMin) - (a.endMin - a.startMin));
    
    // For each event, find how many events it overlaps with that started before it
    cluster.forEach((ev, idx) => {
      // Count concurrent events (started before this one and still running)
      let stackLevel = 0;
      for (let i = 0; i < idx; i++) {
        const prev = cluster[i];
        // If previous event overlaps with current (prev ends after current starts)
        if (prev.endMin > ev.startMin) {
          stackLevel++;
        }
      }
      
      ev.stackLevel = stackLevel;
      ev.maxStack = cluster.length;
      
      // Calculate indent: each level indents by OVERLAP_INDENT_PX
      // But limit indent so event is still at least 60% width
      const maxIndent = 3; // Max 3 levels of indent (then we wrap)
      const effectiveLevel = stackLevel % (maxIndent + 1);
      ev.indentPx = effectiveLevel * OVERLAP_INDENT_PX;
      
      finalEvents.push(ev);
    });
  });

  return finalEvents;
};

// Helper to calculate multi-day event spans for a week row
// Returns array of { event, startCol, spanCols, row } for positioning
const calculateMultiDaySpans = (weekDays, allEvents, formatDateString) => {
  const spans = [];
  const processedEventIds = new Set();
  
  // Get date strings for the week
  const weekDateStrs = weekDays.map(d => formatDateString(d.date || d));
  
  // Find all multi-day events that touch this week
  const multiDayEvents = allEvents.filter(e => {
    const startDate = e.startDate || e.date;
    const endDate = e.endDate || e.date || startDate;
    // Is it actually multi-day?
    const isMultiDay = startDate && endDate && startDate !== endDate;
    if (isMultiDay) {
      devLog('[MultiDay] Found multi-day event:', e.name || e.title, 'from', startDate, 'to', endDate);
    }
    return isMultiDay;
  });
  
  multiDayEvents.forEach(event => {
    const eventId = event.id || event.googleEventId;
    if (processedEventIds.has(eventId)) return;
    
    const eventStart = event.startDate || event.date;
    const eventEnd = event.endDate || event.date || eventStart;
    
    // Find which columns this event spans in this week
    let startCol = -1;
    let endCol = -1;
    
    for (let i = 0; i < 7; i++) {
      const dayStr = weekDateStrs[i];
      if (dayStr >= eventStart && dayStr <= eventEnd) {
        if (startCol === -1) startCol = i;
        endCol = i;
      }
    }
    
    // If event is visible in this week
    if (startCol !== -1) {
      processedEventIds.add(eventId);
      spans.push({
        event,
        startCol,
        spanCols: endCol - startCol + 1,
        isStart: weekDateStrs[startCol] === eventStart,
        isEnd: weekDateStrs[endCol] === eventEnd,
      });
    }
  });
  
  // Sort by start position, then by span length (longer first)
  spans.sort((a, b) => a.startCol - b.startCol || b.spanCols - a.spanCols);
  
  // Assign rows to avoid overlaps
  spans.forEach(span => {
    let row = 0;
    let placed = false;
    while (!placed) {
      // Check if this row is free for all columns this event needs
      const conflict = spans.some(other => 
        other !== span && 
        other.row === row &&
        !(span.startCol >= other.startCol + other.spanCols || span.startCol + span.spanCols <= other.startCol)
      );
      if (!conflict) {
        span.row = row;
        placed = true;
      } else {
        row++;
      }
    }
  });
  
  return spans;
};

const EventsPage = () => {
  const { 
    isDark, events, addEvent, updateEvent, deleteEvent,
    tasks, goals, updateTask, deleteTask, updateGoal, deleteGoal,
    googleCalendarConnected, disconnectGoogleCalendar,
    googleCalendars, setGoogleCalendars,
    calendars: localCalendars, addCalendar: addCalendarToFirestore, 
    updateCalendar: updateCalendarInFirestore, deleteCalendar: deleteCalendarFromFirestore,
    dataLoading,
    showReconnectPrompt, dismissReconnectPrompt, connectGoogleCalendar: reconnectGoogle,
    isConnectingCalendar: isReconnecting,
  } = useApp();

  // Debug logging for reconnect prompt
  devLog('[EventsPage] showReconnectPrompt:', showReconnectPrompt);

  const { 
    events: googleCalendarEvents, 
    connect: connectGoogleCalendar, 
    sync: syncWithGoogleCalendar,
    loading: googleLoading,
    syncing: syncing,
    createEvent: createGoogleEvent,
    updateEvent: updateGoogleEvent,
    deleteEvent: deleteGoogleEvent
  } = useGoogleCalendar();

  // Use googleLoading for general calendar operations, isReconnecting for reconnect button
  const isConnectingCalendar = googleLoading;

  const { hasFeature, canAddMore, getItemLimit, loading: subscriptionLoading } = useSubscription();
  const canConnectGoogle = hasFeature('googleCalendar');
  
  // Check if user can add more events
  const eventLimit = getItemLimit('maxEvents');
  const canAddEvent = canAddMore('maxEvents', events.length);
  const isAtEventLimit = !canAddEvent && eventLimit !== Infinity;
  
  const colors = getColors(isDark);
  // Calendar items are drawn on paper and outlined in their calendar's color, with ink text:
  // the color identifies the calendar without a block of pastel fill.
  const itemRule = (color) => `1px solid color-mix(in srgb, ${color} 70%, transparent)`;
  const itemTint = (color) => `color-mix(in srgb, ${color} 8%, ${colors.bg})`;
  
  // Ref to track if catch-up sync is already running (prevents duplicates)
  const catchUpSyncInProgress = useRef(false);
  // Ref to track recently synced event IDs with their Google IDs (for immediate deduplication)
  // Firestore listener may be slow to update React state, so we use this as a cache
  const recentlySyncedEventIds = useRef(new Map());
  
  // Calendar management - calendars now come from AppContext (Firestore)
  const [showAddCalendar, setShowAddCalendar] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [calendarForm, setCalendarForm] = useState({ name: '', color: CALENDAR_COLORS[0], syncToGoogle: false });
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  
  // Task and Goal calendar settings
  const [taskSettings, setTaskSettings] = useState(getSavedTaskSettings);
  const [goalSettings, setGoalSettings] = useState(getSavedGoalSettings);
  const [editingSpecialCalendar, setEditingSpecialCalendar] = useState(null); // 'tasks' or 'goals'
  const [editingGoogleCalendar, setEditingGoogleCalendar] = useState(null); // Google calendar object being edited
  
  // URL params for navigation from Overview
  const [searchParams, setSearchParams] = useSearchParams();
  
  // View state - initialize from URL params
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      // Parse as local date to avoid timezone issues (YYYY-MM-DD)
      const parts = dateParam.split('-');
      if (parts.length === 3) {
        const parsed = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        return isNaN(parsed.getTime()) ? new Date() : parsed;
      }
    }
    return new Date();
  });
  // Desktop sidebar (create button, mini month, calendars) can fold away to give the grid its width
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return localStorage.getItem('mind_calendar_sidebar') !== 'closed'; } catch { return true; }
  });
  const toggleSidebar = () => {
    setSidebarOpen(open => {
      try { localStorage.setItem('mind_calendar_sidebar', open ? 'closed' : 'open'); } catch { /* private mode */ }
      return !open;
    });
  };

  const [viewMode, setViewMode] = useState(() => {
    const viewParam = searchParams.get('view');
    return ['month', 'week', 'day', 'schedule'].includes(viewParam) ? viewParam : 'month';
  });
  
  // Refs for scrollable time grids
  const weekScrollRef = useRef(null);
  const dayScrollRef = useRef(null);
  
  // Ref to hold latest events for sync logic
  const eventsRef = useRef(events);
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const [selectedDate, setSelectedDate] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      // Parse as local date to avoid timezone issues (YYYY-MM-DD)
      const parts = dateParam.split('-');
      if (parts.length === 3) {
        const parsed = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        return isNaN(parsed.getTime()) ? new Date() : parsed;
      }
    }
    return new Date();
  });
  
  // Modal state
  const [showEventModal, setShowEventModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [eventForm, setEventForm] = useState({
    name: '', startDate: '', endDate: '', startTime: '', endTime: '',
    description: '', location: '', calendarId: 'default', color: CALENDAR_COLORS[0],
    isAllDay: false
  });

  // Task edit modal state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', dueDate: '', category: 'personal', weight: 'medium'
  });

  // Goal edit modal state  
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({
    title: '', timeframe: 'monthly', targetDate: '',
    specific: '', measurable: '', achievable: '', relevant: '', timeBound: ''
  });

  // Google Calendar sync
  // const [syncing, setSyncing] = useState(false); // Removed: using hook state
  const [syncingSpecialCalendar, setSyncingSpecialCalendar] = useState(false);
  
  // Preview modal state (read-only view before edit)
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewItem, setPreviewItem] = useState(null); // { type: 'event'|'task'|'goal', data: {...} }
  
  // Drag-to-create event state
  const [dragCreate, setDragCreate] = useState(null); // { date, startY, startHour, currentHour }
  const dragCreateRef = useRef(null);
  const dragCreateDataRef = useRef(null); // Ref to track latest dragCreate for event handlers
  const justFinishedDragRef = useRef(false); // Track if we just finished a drag to prevent click handler
  
  // Mobile mini calendar toggle
  const [showMobileMiniCal, setShowMobileMiniCal] = useState(true);
  
  // Mobile calendar manager modal
  const [showMobileCalendarManager, setShowMobileCalendarManager] = useState(false);
  
  // Tablet view mode dropdown
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  
  // Calendars are now saved to Firestore via AppContext, no localStorage needed

  // Save task/goal settings to localStorage
  useEffect(() => {
    localStorage.setItem('taskCalendarSettings', JSON.stringify(taskSettings));
  }, [taskSettings]);

  useEffect(() => {
    localStorage.setItem('goalCalendarSettings', JSON.stringify(goalSettings));
  }, [goalSettings]);

  // Save Google calendar visibility to localStorage
  useEffect(() => {
    if (googleCalendars.length > 0) {
      const visibility = {};
      googleCalendars.forEach(cal => {
        visibility[cal.id] = cal.visible;
      });
      localStorage.setItem('googleCalendarVisibility', JSON.stringify(visibility));
    }
  }, [googleCalendars]);

  // Re-fetch Google Calendar events when navigating to different months
  // Handled by useGoogleCalendar hook now
  useEffect(() => {
    // No-op: Events are synced via Firestore
  }, [currentDate]);

  // While this page is open and on screen, sync with Google every two minutes
  // (the server also syncs everyone every 15). Each sync reads every calendar,
  // so not more often than that
  useEffect(() => {
    if (!googleCalendarConnected) return undefined;
    const pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible') syncWithGoogleCalendar();
    }, 2 * 60 * 1000);
    return () => clearInterval(pollInterval);
  }, [googleCalendarConnected, syncWithGoogleCalendar]);

  // Handle openItem from sessionStorage (passed from Dashboard)
  useEffect(() => {
    // Check sessionStorage for item to open
    const storedItem = sessionStorage.getItem('openCalendarItem');
    
    if (storedItem) {
      // Clear immediately to prevent reopening on re-renders
      sessionStorage.removeItem('openCalendarItem');
      
      try {
        const { id: openItemId, itemType } = JSON.parse(storedItem);
        
        let item = null;
        
        if (itemType === 'task') {
          item = tasks.find(t => t.id === openItemId);
          if (item) {
            setPreviewItem({ type: 'task', data: item });
            setShowPreviewModal(true);
          }
        } else if (itemType === 'goal') {
          item = goals.find(g => g.id === openItemId);
          if (item) {
            setPreviewItem({ type: 'goal', data: item });
            setShowPreviewModal(true);
          }
        } else if (itemType === 'event') {
          // Check both local events and Google calendar events
          item = events.find(e => e.id === openItemId);
          if (!item) {
            item = googleCalendarEvents.find(e => e.id === openItemId || e.googleEventId === openItemId);
          }
          if (item) {
            setPreviewItem({ type: 'event', data: item });
            setShowPreviewModal(true);
          }
        }
      } catch (e) {
        devError('[EventsPage] Error parsing stored item:', e);
      }
    }
  }, [tasks, goals, events, googleCalendarEvents]);

  // Sync URL with current date and view mode
  useEffect(() => {
    const dateToUse = viewMode === 'day' || viewMode === 'week' ? selectedDate : currentDate;
    const dateStr = formatDateString(dateToUse);
    const currentUrlDate = searchParams.get('date');
    const currentUrlView = searchParams.get('view');
    
    // Only update if changed to avoid infinite loops
    if (dateStr !== currentUrlDate || viewMode !== currentUrlView) {
      setSearchParams({ date: dateStr, view: viewMode }, { replace: true });
    }
  }, [currentDate, selectedDate, viewMode, searchParams, setSearchParams]);

  // Helper functions
  const formatDateString = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  };

  const formatTimeShort = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'p' : 'a';
    const hour12 = h % 12 || 12;
    return minutes === '00' ? `${hour12}${ampm}` : `${hour12}:${minutes}${ampm}`;
  };

  // Get week days for current week
  const weekDays = useMemo(() => {
    const date = selectedDate || currentDate;
    const dayOfWeek = date.getDay();
    const sunday = new Date(date);
    sunday.setDate(date.getDate() - dayOfWeek);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(sunday);
      d.setDate(sunday.getDate() + i);
      days.push(d);
    }
    return days;
  }, [selectedDate, currentDate]);

  // Get calendar days for the current month
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    const prevMonth = new Date(year, month, 0);
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonth.getDate() - i), isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  }, [currentDate]);

  // Mini calendar days
  const miniCalendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startPadding = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    const prevMonth = new Date(year, month, 0);
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonth.getDate() - i), isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remaining = 35 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  }, [currentDate]);

  // Get visible calendar IDs
  const visibleCalendarIds = useMemo(() => {
    const local = localCalendars.filter(c => c.visible).map(c => c.id);
    const google = googleCalendars.filter(c => c.visible).map(c => c.id);
    return new Set([...local, ...google]);
  }, [localCalendars, googleCalendars]);

  // Helper to get event color with access to local calendars from context
  const getEventColorWithCalendars = useCallback((event) => {
    return getEventColor(event, { localCalendars });
  }, [localCalendars]);

  // Get events for a specific date (combines local + Google events)
  const getEventsForDate = useCallback((date) => {
    const dateStr = formatDateString(date);

    // Load cached sync map from localStorage (survives page refresh)
    const cachedSyncMap = getSyncedEventCache();
    
    // Build set of synced event IDs to filter out duplicates
    // (tasks, goals, and events that were synced to Google Calendar)
    const taskGoogleIds = (tasks || []).filter((t) => t.googleEventId).map((t) => t.googleEventId);
    const goalGoogleIds = (goals || []).filter((g) => g.googleEventId).map((g) => g.googleEventId);
    const eventGoogleIds = (events || []).filter((e) => e.googleEventId).map((e) => e.googleEventId);
    // Also include recently synced event IDs (in-memory and cached)
    const recentlySyncedIds = Array.from(recentlySyncedEventIds.current.values());
    const cachedSyncIds = Object.values(cachedSyncMap);
    const syncedEventIds = new Set([...taskGoogleIds, ...goalGoogleIds, ...eventGoogleIds, ...recentlySyncedIds, ...cachedSyncIds]);

    // Also build signature map for fallback deduplication (if googleEventId is missing)
    const localEventSignatures = new Map();
    [...(tasks || []), ...(goals || []), ...(events || [])]
      .forEach(item => {
        if (item.googleEventId) {
          const sig = eventSignature(item);
          localEventSignatures.set(sig, item.googleEventId);
        }
      });

    // Local events from Firebase (excluding those already synced to Google)
    const localEvents = events.filter((e) => {
      const eventStart = e.startDate || e.date;
      const eventEnd = e.endDate || e.date || eventStart;
      const inDateRange = dateStr >= eventStart && dateStr <= eventEnd;
      const calendarVisible = visibleCalendarIds.has(e.calendarId || 'default');
      
      if (e.name === 'Creating Local only event. - sync off') {
        devLog('[DedupeDebug] Local event:', {
          name: e.name,
          googleEventId: e.googleEventId,
          inDateRange,
          calendarVisible
        });
      }
      
      // IMPORTANT: Show ALL local events regardless of sync status
      // The sync system handles duplicates via the googleEventId matching
      // Don't hide just because an event was synced to Google - show it!
      
      return inDateRange && calendarVisible;
    });

    // Google Calendar events (excluding synced tasks/goals to avoid duplicates)
    const googleEvents = googleCalendarEvents.filter((e) => {
      const eventStart = e.date;
      const eventEnd = e.endDate || e.date; // For multi-day events
      const inDateRange = dateStr >= eventStart && dateStr <= eventEnd;
      const calendarVisible = visibleCalendarIds.has(e.calendarId);
      
      // Only filter out if this Google event ID matches an event in our LOCAL synced items
      // (tasks, goals, or events that were synced to Google)
      // This prevents showing the same event twice when it exists in both places
      const googleId = e.googleEventId || e.id;
      const isLocalSyncedItem = syncedEventIds.has(googleId);
      
      // Fallback deduplication: check if event signature matches any synced local event
      // This handles cases where googleEventId is missing from the local event
      const sig = eventSignature(e);
      const isLocalBySignature = localEventSignatures.has(sig);
      
      return inDateRange && calendarVisible && !isLocalSyncedItem && !isLocalBySignature;
    });

    // Combine and sort by time
    return [...localEvents, ...googleEvents].sort((a, b) => {
      const aTime = a.startTime || '00:00';
      const bTime = b.startTime || '00:00';
      return aTime.localeCompare(bTime);
    });
  }, [events, googleCalendarEvents, visibleCalendarIds, tasks, goals]);

  // Get all-day events for a date
  const getAllDayEvents = useCallback((date) => {
    return getEventsForDate(date).filter(e => !e.startTime);
  }, [getEventsForDate]);

  // Get timed events for a date
  const getTimedEvents = useCallback((date) => {
    return getEventsForDate(date).filter(e => e.startTime);
  }, [getEventsForDate]);

  // Get tasks for a date
  const getTasksForDate = useCallback((date) => {
    const dateStr = formatDateString(date);
    return tasks.filter(t => t.dueDate === dateStr && !t.completed);
  }, [tasks]);

  // Get goals for a date
  const getGoalsForDate = useCallback((date) => {
    const dateStr = formatDateString(date);
    return goals.filter(g => !g.completed && g.targetDate === dateStr);
  }, [goals]);

  // Get upcoming events for schedule view (next 60 days)
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 60);
    
    const dateMap = new Map();
    
    for (let d = new Date(today); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = formatDateString(d);
      const dayEvents = getEventsForDate(new Date(d));
      const dayTasks = getTasksForDate(new Date(d));
      const dayGoals = getGoalsForDate(new Date(d));
      
      if (dayEvents.length > 0 || dayTasks.length > 0 || dayGoals.length > 0) {
        dateMap.set(dateStr, {
          date: new Date(d),
          events: dayEvents,
          tasks: dayTasks,
          goals: dayGoals
        });
      }
    }
    
    return Array.from(dateMap.values());
  }, [getEventsForDate, getTasksForDate, getGoalsForDate]);

  // Navigation
  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const navigate = (direction) => {
    if (viewMode === 'month') {
      const newDate = new Date(currentDate);
      newDate.setMonth(newDate.getMonth() + direction);
      setCurrentDate(newDate);
    } else if (viewMode === 'schedule') {
      const newDate = new Date(currentDate);
      newDate.setMonth(newDate.getMonth() + direction);
      setCurrentDate(newDate);
    } else if (viewMode === 'week') {
      const baseDate = selectedDate || currentDate;
      const newDate = new Date(baseDate);
      newDate.setDate(newDate.getDate() + (direction * 7));
      setSelectedDate(newDate);
      setCurrentDate(newDate);
    } else if (viewMode === 'day') {
      const baseDate = selectedDate || currentDate;
      const newDate = new Date(baseDate);
      newDate.setDate(newDate.getDate() + direction);
      setSelectedDate(newDate);
      setCurrentDate(newDate);
    }
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };

  const isSameDate = (d1, d2) => {
    return d1.getDate() === d2.getDate() && 
           d1.getMonth() === d2.getMonth() && 
           d1.getFullYear() === d2.getFullYear();
  };

  // Toggle calendar visibility
  const toggleCalendarVisibility = async (calendarId, isGoogle = false) => {
    if (isGoogle) {
      setGoogleCalendars(prev => {
        const updated = prev.map(c => 
          c.id === calendarId ? { ...c, visible: !c.visible } : c
        );
        // Persist the visibility change
        const calendar = updated.find(c => c.id === calendarId);
        if (calendar) {
          setGoogleCalendarVisibility(calendarId, calendar.visible);
        }
        return updated;
      });
    } else {
      // Update visibility in Firestore
      const calendar = localCalendars.find(c => c.id === calendarId);
      if (calendar) {
        await updateCalendarInFirestore(calendarId, { visible: !calendar.visible });
      }
    }
  };

  // Scroll to first event in the current view, or current time if no events
  const scrollToFirstEvent = useCallback(() => {
    const scrollContainer = weekScrollRef.current || dayScrollRef.current;
    if (!scrollContainer) return;
    
    // Get all timed events for the current week/day
    let allTimedEvents = [];
    if (viewMode === 'week') {
      weekDays.forEach(day => {
        allTimedEvents.push(...getTimedEvents(day));
      });
    } else if (viewMode === 'day' && selectedDate) {
      allTimedEvents = getTimedEvents(selectedDate);
    }
    
    // Find the earliest event
    let targetHour = 8; // Default to 8 AM if no events
    
    if (allTimedEvents.length > 0) {
      // Find earliest start time
      let earliestMinutes = Infinity;
      allTimedEvents.forEach(event => {
        if (event.startTime) {
          const [h, m] = event.startTime.split(':').map(Number);
          const totalMinutes = h * 60 + m;
          if (totalMinutes < earliestMinutes) {
            earliestMinutes = totalMinutes;
          }
        }
      });
      if (earliestMinutes !== Infinity) {
        // Scroll to 1 hour before the first event, clamped to 0
        targetHour = Math.max(0, Math.floor(earliestMinutes / 60) - 1);
      }
    } else {
      // No events - scroll to current time or 8 AM
      const now = new Date();
      targetHour = Math.max(0, now.getHours() - 1);
    }
    
    // Scroll to target position (each hour is 60px)
    const scrollPosition = targetHour * 60;
    scrollContainer.scrollTo({ top: scrollPosition, behavior: 'smooth' });
  }, [viewMode, weekDays, selectedDate]);

  // Scroll to first event when switching to week/day view
  useEffect(() => {
    if (viewMode === 'week' || viewMode === 'day') {
      // Wait for the view to render
      setTimeout(() => scrollToFirstEvent(), 150);
    }
  }, [viewMode, scrollToFirstEvent]);

  // Open calendar edit modal
  const openCalendarEdit = (calendar) => {
    // Don't open modal until data is fully loaded from Firestore
    if (dataLoading) return;
    
    // Get the latest version of this calendar from state to ensure we have the most recent syncToGoogle value
    const latestCalendar = localCalendars.find(c => c.id === calendar.id) || calendar;
    const formValue = latestCalendar.syncToGoogle === true;
    
    setEditingCalendar(latestCalendar);
    setCalendarForm({ 
      name: latestCalendar.name, 
      color: latestCalendar.color, 
      syncToGoogle: formValue
    });
    setShowAddCalendar(true);
  };

  // Save calendar (add or edit)
  const handleSaveCalendar = async () => {
    if (!calendarForm.name.trim()) return;
    
    setSyncingCalendar(true);
    
    try {
      if (editingCalendar) {
        // Check if sync setting changed
        const wasSync = editingCalendar.syncToGoogle || false;
        const nowSync = calendarForm.syncToGoogle;
        
        let googleCalendarId = editingCalendar.googleCalendarId;
        
        // If enabling sync, check if we need to create/recreate the Google Calendar
        if (nowSync && googleCalendarConnected) {
          // IMPORTANT: Refresh Google Calendar list to ensure we have latest calendars
          try {
            await syncWithGoogleCalendar();
          } catch (err) {
            // Don't fail - continue with potentially stale list
          }
          
          // Step 1: Verify stored ID still exists in Google
          if (googleCalendarId) {
            try {
              const checkFn = httpsCallable(functions, 'checkGoogleCalendarExists');
              const checkResult = await checkFn({ calendarId: googleCalendarId });
              if (!checkResult.data.exists) {
                googleCalendarId = null;
              }
            } catch (err) {
              googleCalendarId = null;
            }
          }
          
          // Step 2: If no valid ID, search for existing calendar by name
          // After syncWithGoogleCalendar completes, the Firestore listener updates the googleCalendars state
          // Give it a small delay to ensure the listener has processed the update
          if (!googleCalendarId) {
            const expectedName = `${calendarForm.name.trim()} - mind.`;
            // Wait a moment for the Firestore listener to update with the fresh sync
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Now search the updated googleCalendars state
            const existingCalendar = googleCalendars.find((cal) => cal.name === expectedName);

            if (existingCalendar) {
              googleCalendarId = existingCalendar.id;
            }
          }
          
          // Step 3: If still no ID, create a new Google Calendar
          if (!googleCalendarId) {
            const createFn = httpsCallable(functions, 'createGoogleCalendar');
            const result = await createFn({
              name: `${calendarForm.name.trim()} - mind.`,
              color: calendarForm.color,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            googleCalendarId = result.data.calendarId;
          }
          
          // Sync any unsynced local events to Google Calendar (catch-up sync)
          // This runs whether we just created the calendar or found an existing one
          // Check if catch-up sync is already in progress (prevents duplicate syncs)
          if (catchUpSyncInProgress.current) {
            devLog('[CatchUpSync] Already in progress, skipping');
          } else {
            catchUpSyncInProgress.current = true;
            try {
              const calendarEvents = events.filter(e => e.calendarId === editingCalendar.id && (e.startDate || e.date));
              const unsyncedEvents = calendarEvents.filter(e => !e.googleEventId);
              devLog('[CatchUpSync] Calendar events:', calendarEvents.length, 'Unsynced:', unsyncedEvents.length);
              devLog('[CatchUpSync] calendarEvents details:', calendarEvents.map(e => ({ id: e.id, name: e.name, googleEventId: e.googleEventId })));
              devLog('[CatchUpSync] googleCalendarId:', googleCalendarId);
              if (unsyncedEvents.length > 0) {
                devLog('[CatchUpSync] Starting catch-up sync for', unsyncedEvents.length, 'events');
                const createEventFn = httpsCallable(functions, 'createGoogleEvent');
                const syncedEventIds = new Set(); // Track which events we've synced in this session
                const persistedMap = getSyncedEventCache(); // Load existing cache
                
                for (const event of unsyncedEvents) {
                  // Skip if we've already synced this event in this loop (shouldn't happen, but safety check)
                  if (syncedEventIds.has(event.id)) {
                    devLog('[CatchUpSync] Skipping already-synced event:', event.id);
                    continue;
                  }
                  try {
                    // Format event for Google
                    const googleEvent = {
                      summary: event.name,
                      description: event.description || '',
                      location: event.location || '',
                    };
                    const eventDate = event.startDate || event.date;
                    const endDate = event.endDate || eventDate;
                    if (event.startTime && event.endTime) {
                      googleEvent.start = { dateTime: `${eventDate}T${event.startTime}:00`, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };
                      googleEvent.end = { dateTime: `${endDate}T${event.endTime}:00`, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };
                    } else {
                      googleEvent.start = { date: eventDate };
                      const allDayEndDate = new Date(endDate);
                      allDayEndDate.setDate(allDayEndDate.getDate() + 1);
                      googleEvent.end = { date: allDayEndDate.toISOString().split('T')[0] };
                    }
                    const createResult = await createEventFn({ calendarId: googleCalendarId, event: googleEvent });
                    devLog('[CatchUpSync] Created event result:', createResult.data);
                    // createGoogleEvent returns res.data which has 'id' field (Google event ID)
                    const googleEventId = createResult.data?.id;
                    if (googleEventId) {
                      devLog('[CatchUpSync] Updating local event', event.id, 'with googleEventId:', googleEventId);
                      // Store mapping immediately for deduplication (React state update may be delayed)
                      recentlySyncedEventIds.current.set(event.id, googleEventId);
                      
                      // Persist to localStorage to survive page refresh
                      persistedMap[event.id] = googleEventId;
                      setSyncedEventCache(persistedMap);
                      
                      // Pass skipGoogleSync since we're just setting the ID, not syncing event changes
                      await updateEvent(event.id, { googleEventId: googleEventId }, { skipGoogleSync: true });
                      syncedEventIds.add(event.id); // Mark as synced
                    }
                  } catch (err) {
                    devError('[CatchUpSync] Error syncing event:', event.name, err);
                    // Continue with other events even if one fails
                  }
                }
                // Wait a bit to allow Firestore listener to process all updates before next check
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            } finally {
              catchUpSyncInProgress.current = false;
            }
          }
        }
        
        // If name or color changed and calendar is synced, update Google Calendar
        if (nowSync && googleCalendarId && googleCalendarConnected) {
          const nameChanged = editingCalendar.name !== calendarForm.name.trim();
          const colorChanged = editingCalendar.color !== calendarForm.color;
          devLog('[ColorSync] Checking for changes:', { 
            nameChanged, colorChanged,
            oldName: editingCalendar.name, newName: calendarForm.name.trim(),
            oldColor: editingCalendar.color, newColor: calendarForm.color 
          });
          
          if (nameChanged || colorChanged) {
            devLog('[ColorSync] Updating Google Calendar...');
            try {
              const updateFn = httpsCallable(functions, 'updateGoogleCalendar');
              await updateFn({
                calendarId: googleCalendarId,
                name: nameChanged ? `${calendarForm.name.trim()} - mind.` : undefined,
                color: colorChanged ? calendarForm.color : undefined
              });
              devLog('[ColorSync] Success!');
            } catch (err) {
              devError('[ColorSync] Error:', err);
            }
          }
        }
        
        // Update existing calendar in Firestore
        const updatePayload = {
          name: calendarForm.name.trim(), 
          color: calendarForm.color,
          syncToGoogle: calendarForm.syncToGoogle === true,
          googleCalendarId: googleCalendarId
        };
        await updateCalendarInFirestore(editingCalendar.id, updatePayload);
        
        // Small delay to ensure Firestore write completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Update events that belong to this calendar with new color
        events.forEach(e => {
          if (e.calendarId === editingCalendar.id) {
            updateEvent(e.id, { color: calendarForm.color });
          }
        });
      } else {
        // Add new calendar
        let googleCalendarId = null;

        // If sync is enabled, check for existing Google Calendar first
        if (calendarForm.syncToGoogle && googleCalendarConnected) {
          const expectedName = `${calendarForm.name.trim()} - mind.`;

          // Check if a calendar with this name already exists
          const existingCalendar = googleCalendars.find((cal) => cal.name === expectedName);

          if (existingCalendar) {
            googleCalendarId = existingCalendar.id;
          } else {
            // Create new Google Calendar if one doesn't exist
            const createFn = httpsCallable(functions, 'createGoogleCalendar');
            const result = await createFn({
              name: expectedName,
              color: calendarForm.color,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            googleCalendarId = result.data.calendarId;
          }
        }

        // Add new calendar to Firestore
        await addCalendarToFirestore({
          name: calendarForm.name.trim(),
          color: calendarForm.color,
          visible: true,
          syncToGoogle: calendarForm.syncToGoogle,
          googleCalendarId: googleCalendarId
        });
      }
    } catch (error) {
      alert('Failed to sync with Google Calendar: ' + error.message);
    } finally {
      setSyncingCalendar(false);
      setCalendarForm({ name: '', color: CALENDAR_COLORS[0], syncToGoogle: false });
      setEditingCalendar(null);
      setShowAddCalendar(false);
    }
  };

  // Delete local calendar (uses Firestore)
  const deleteCalendar = async (calendarId) => {
    if (calendarId === 'default') return;
    // Move events from this calendar to default
    for (const e of events) {
      if (e.calendarId === calendarId) {
        await updateEvent(e.id, { calendarId: 'default' });
      }
    }
    // Delete from Firestore
    await deleteCalendarFromFirestore(calendarId);
  };

  // Open add event modal
  const openAddModal = (date, time = null, isAllDay = false) => {
    // Check limit before allowing add
    if (isAtEventLimit) {
      setShowUpgradeModal(true);
      return;
    }
    
    const dateStr = formatDateString(date || new Date());
    // Calculate end time: 1 hour after start, preserving minutes
    let endTime = '';
    if (!isAllDay && time) {
      const [hours, minutes] = time.split(':');
      const endHour = Math.min(parseInt(hours) + 1, 23);
      endTime = `${String(endHour).padStart(2, '0')}:${minutes}`;
    }
    
    setEditingEvent(null);
    setEventForm({
      name: '', startDate: dateStr, endDate: dateStr,
      startTime: isAllDay ? '' : (time || ''), 
      endTime: isAllDay ? '' : endTime,
      description: '', location: '', calendarId: 'default',
      color: localCalendars[0]?.color || CALENDAR_COLORS[0],
      isAllDay: isAllDay
    });
    setShowEventModal(true);
  };

  // Open add modal with specific start/end times (from drag-to-create)
  const openAddModalWithTimes = (date, startTime, endTime) => {
    devLog('[DragCreate] openAddModalWithTimes called:', { date, startTime, endTime });
    if (isAtEventLimit) {
      setShowUpgradeModal(true);
      return;
    }
    const dateStr = formatDateString(date || new Date());
    devLog('[DragCreate] openAddModalWithTimes setting form with:', { dateStr, startTime, endTime });
    setEditingEvent(null);
    setEventForm({
      name: '', startDate: dateStr, endDate: dateStr,
      startTime: startTime,
      endTime: endTime,
      description: '', location: '', calendarId: 'default',
      color: localCalendars[0]?.color || CALENDAR_COLORS[0],
      isAllDay: false
    });
    setShowEventModal(true);
  };

  // Keep ref in sync with state for event handlers
  useEffect(() => {
    dragCreateDataRef.current = dragCreate;
  }, [dragCreate]);

  // Global mouse/touch move and end handlers for drag-to-create
  useEffect(() => {
    if (!dragCreate) return;
    
    const handleMove = (e) => {
      if (!dragCreateDataRef.current || !dragCreateRef.current) return;
      
      const rect = dragCreateRef.current.getBoundingClientRect();
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
      const hour = Math.floor(Math.max(0, Math.min(y, 24 * 60 - 1)) / 60);
      const minutes = Math.floor((Math.max(0, y) % 60) / 15) * 15;
      
      // Update ref SYNCHRONOUSLY so handleEnd always has latest values
      const newDragCreate = {
        ...dragCreateDataRef.current,
        currentHour: hour,
        currentMinutes: minutes
      };
      dragCreateDataRef.current = newDragCreate;
      
      // Also update state for visual rendering
      setDragCreate(newDragCreate);
    };
    
    const handleEnd = () => {
      // Use ref to get latest state value (avoids stale closure)
      const currentDragCreate = dragCreateDataRef.current;
      devLog('[DragCreate] handleEnd - ref value:', currentDragCreate);
      if (!currentDragCreate) return;
      
      const { date, startHour, startMinutes, currentHour, currentMinutes } = currentDragCreate;
      devLog('[DragCreate] handleEnd - extracted values:', { startHour, startMinutes, currentHour, currentMinutes });
      
      // Calculate start and end times
      let startTotal = startHour * 60 + startMinutes;
      let endTotal = currentHour * 60 + currentMinutes;
      devLog('[DragCreate] handleEnd - totals before swap:', { startTotal, endTotal });
      
      // Swap if dragged upward
      if (endTotal < startTotal) {
        [startTotal, endTotal] = [endTotal, startTotal];
      }
      
      // Ensure minimum 15-minute duration
      if (endTotal - startTotal < 15) {
        endTotal = startTotal + 15;
      }
      devLog('[DragCreate] handleEnd - totals after adjustments:', { startTotal, endTotal });
      
      const startH = Math.floor(startTotal / 60);
      const startM = startTotal % 60;
      const endH = Math.floor(Math.min(endTotal, 24 * 60 - 1) / 60);
      const endM = Math.min(endTotal, 24 * 60 - 1) % 60;
      
      const startTime = `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
      const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
      devLog('[DragCreate] handleEnd - final times:', { startTime, endTime });
      
      // Set flag to prevent onClick from also firing
      justFinishedDragRef.current = true;
      setTimeout(() => { justFinishedDragRef.current = false; }, 0);
      
      setDragCreate(null);
      dragCreateRef.current = null;
      dragCreateDataRef.current = null;
      
      openAddModalWithTimes(date, startTime, endTime);
    };
    
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [dragCreate, isAtEventLimit, localCalendars]);

  // Open preview modal (read-only view)
  const openPreviewEvent = (event) => {
    setPreviewItem({ type: 'event', data: event });
    setShowPreviewModal(true);
  };

  const openPreviewTask = (task) => {
    setPreviewItem({ type: 'task', data: task });
    setShowPreviewModal(true);
  };

  const openPreviewGoal = (goal) => {
    setPreviewItem({ type: 'goal', data: goal });
    setShowPreviewModal(true);
  };

  // Check if an item is editable
  const isItemEditable = (item, type) => {
    if (type === 'event') {
      // Check if it's a Google event that's not editable
      if (item.isGoogleEvent) {
        // Birthday events and read-only calendar events cannot be edited
        if (item.canEdit === false) return false;
        const googleCal = googleCalendars.find(c => c.id === item.calendarId);
        if (googleCal && !googleCal.canWrite) return false;
      }
      return true;
    }
    // Tasks and goals are always editable
    return true;
  };

  // Switch from preview to edit
  const switchToEdit = () => {
    if (!previewItem) return;
    setShowPreviewModal(false);
    
    if (previewItem.type === 'event') {
      openEditModal(previewItem.data);
    } else if (previewItem.type === 'task') {
      openEditTaskModal(previewItem.data);
    } else if (previewItem.type === 'goal') {
      openEditGoalModal(previewItem.data);
    }
  };

  // Open edit event modal
  const openEditModal = (event) => {
    setEditingEvent(event);
    // Determine if this is an all-day event (no start time, or explicitly marked as all-day)
    const isAllDay = event.isAllDay || (!event.startTime && !event.endTime);
    setEventForm({
      name: event.name,
      startDate: event.startDate || event.date || '',
      endDate: event.endDate || event.date || '',
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      description: event.description || '',
      location: event.location || '',
      calendarId: event.calendarId || 'default',
      color: getEventColorWithCalendars(event),
      isAllDay: isAllDay
    });
    setShowEventModal(true);
  };

  // Handle form submit
  const handleSubmit = async () => {
    devLog('[EventCreate] handleSubmit called');
    devLog('[EventCreate] eventForm:', eventForm);
    
    if (!eventForm.name.trim() || !eventForm.startDate) {
      devLog('[EventCreate] Validation failed - name:', eventForm.name, 'startDate:', eventForm.startDate);
      return;
    }
    
    setSavingEvent(true);
    
    // Check if this is a Google calendar
    const googleCalendar = googleCalendars.find(c => c.id === eventForm.calendarId);
    const localCalendar = localCalendars.find(c => c.id === eventForm.calendarId);
    
    devLog('[EventCreate] Google calendar:', googleCalendar);
    devLog('[EventCreate] Local calendar:', localCalendar);
    
    // Make sure the target calendar is visible so the user can see the new event
    if (localCalendar && !localCalendar.visible) {
      devLog('[EventCreate] Making local calendar visible:', localCalendar.id);
      await updateCalendarInFirestore(localCalendar.id, { visible: true });
    }
    
    const eventData = {
      ...eventForm,
      date: eventForm.startDate,
      color: googleCalendar?.color || localCalendar?.color || eventForm.color,
    };
    devLog('[EventCreate] eventData to save:', eventData);
    
    try {
      // If targeting a Google calendar, create/update on Google
      if (googleCalendar && googleCalendarConnected) {
        
        // Convert to Google Event format
        let googleEventBody;
        
        if (eventForm.isAllDay || (!eventData.startTime && !eventData.endTime)) {
          // All-day event - use date format
          // Google Calendar uses exclusive end dates for all-day events
          // So if event is Jan 4, end date should be Jan 5
          const startDate = eventData.startDate;
          const endDate = eventData.endDate || eventData.startDate;
          
          // Add one day to end date for Google's exclusive end date format
          const endDateObj = new Date(endDate + 'T00:00:00');
          endDateObj.setDate(endDateObj.getDate() + 1);
          const exclusiveEndDate = endDateObj.toISOString().split('T')[0];
          
          googleEventBody = {
            summary: eventData.name,
            description: eventData.description,
            location: eventData.location,
            start: { date: startDate },
            end: { date: exclusiveEndDate },
          };
        } else {
          // Timed event - use dateTime format
          googleEventBody = {
            summary: eventData.name,
            description: eventData.description,
            location: eventData.location,
            start: {
              dateTime: new Date(`${eventData.startDate}T${eventData.startTime}`).toISOString(),
            },
            end: {
              dateTime: new Date(`${eventData.endDate || eventData.startDate}T${eventData.endTime}`).toISOString(),
            },
          };
        }

        if (editingEvent && editingEvent.isGoogleEvent && editingEvent.googleEventId) {
          // Update existing Google event
          devLog('[EventCreate] Updating Google Calendar event:', editingEvent.googleEventId);
          await updateGoogleEvent(editingEvent.googleEventId, googleEventBody, googleCalendar.id);
        } else {
          // Create new Google event
          devLog('[EventCreate] Creating event on Google Calendar:', googleCalendar.name);
          await createGoogleEvent(googleEventBody, googleCalendar.id);
        }
      } else {
        // Local calendar - save to Firebase
        if (editingEvent) {
          devLog('[EventCreate] Updating event:', editingEvent.id);
          await updateEvent(editingEvent.id, eventData);
        } else {
          devLog('[EventCreate] Adding new event...');
          const result = await addEvent(eventData);
          devLog('[EventCreate] addEvent result:', result);
        }
      }
      devLog('[EventCreate] Success! Closing modal.');
      setShowEventModal(false);
      setEditingEvent(null);
      setSavingEvent(false);
    } catch (error) {
      devError('[EventCreate] Error:', error);
      setSavingEvent(false);
    }
  };

  // Handle delete event with optimistic UI update
  const handleDeleteEvent = async (eventToDelete) => {
    // OPTIMISTIC UPDATE: Immediately remove from UI for instant feedback
    // Store original in case we need to rollback
    const originalEvents = [...events];
    
    // Check if this is a pure Google Calendar event (from google_events collection)
    const isPureGoogleEvent = eventToDelete.googleEventId && 
      !events.find(e => e.id === eventToDelete.id);
    
    if (!isPureGoogleEvent) {
      // For local events, optimistically remove from state immediately
      // This gives instant UI feedback
      const optimisticEvents = events.filter(e => e.id !== eventToDelete.id);
      // Note: setEvents is handled by Firestore listener, but we can trigger
      // the delete immediately for faster perceived performance
    }
    
    try {
      if (isPureGoogleEvent && googleCalendarConnected) {
        // Direct deletion of pure Google event
        await deleteGoogleEvent(eventToDelete.googleEventId, eventToDelete.calendarId);
      } else if (eventToDelete.googleEventId && googleCalendarConnected) {
        // Local event that has been synced to Google - delete from both
        const calendar = localCalendars?.find(c => c.id === eventToDelete.calendarId);
        
        // Delete local first (fast), then Google in background
        // This gives immediate UI response
        const localDeletePromise = deleteEvent(eventToDelete.id, { skipGoogleSync: true });
        
        if (calendar?.googleCalendarId) {
          // Fire Google delete in parallel, don't wait
          deleteGoogleEvent(eventToDelete.googleEventId, calendar.googleCalendarId)
            .catch(error => devLog('[Delete] Google deletion failed, but local is removed'));
        }
        
        await localDeletePromise;
        return; // Already handled
      }
      
      // Delete from local Firestore (non-synced events)
      await deleteEvent(eventToDelete.id, { skipGoogleSync: true });
    } catch (error) {
      devError('[Delete] Error during deletion:', error);
      // On error, the Firestore listener will restore correct state
    }
  };

  // Open edit task modal
  const openEditTaskModal = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      dueDate: task.dueDate || '',
      category: task.category || 'personal',
      weight: task.weight || 'medium'
    });
    setShowTaskModal(true);
  };

  // Handle task save
  const handleTaskSave = async () => {
    if (!taskForm.title.trim()) return;
    await updateTask(editingTask.id, taskForm);
    setShowTaskModal(false);
    setEditingTask(null);
  };

  // Handle task delete
  const handleTaskDelete = async () => {
    await deleteTask(editingTask.id);
    setShowTaskModal(false);
    setEditingTask(null);
  };

  // Open edit goal modal
  const openEditGoalModal = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      title: goal.title || '',
      timeframe: goal.timeframe || 'monthly',
      targetDate: goal.targetDate || '',
      specific: goal.specific || '',
      measurable: goal.measurable || '',
      achievable: goal.achievable || '',
      relevant: goal.relevant || '',
      timeBound: goal.timeBound || ''
    });
    setShowGoalModal(true);
  };

  // Handle goal save
  const handleGoalSave = async () => {
    if (!goalForm.title.trim()) return;
    await updateGoal(editingGoal.id, goalForm);
    setShowGoalModal(false);
    setEditingGoal(null);
  };

  // Handle goal delete
  const handleGoalDelete = async () => {
    await deleteGoal(editingGoal.id);
    setShowGoalModal(false);
    setEditingGoal(null);
  };

  // Two-way sync with Google Calendar
  // Get header text based on view
  const getHeaderText = () => {
    if (viewMode === 'day') {
      return selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    if (viewMode === 'week') {
      const start = weekDays[0];
      const end = weekDays[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.toLocaleDateString('en-US', { month: 'long' })} ${start.getFullYear()}`;
      }
      return `${start.toLocaleDateString('en-US', { month: 'short' })} – ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
    }
    if (viewMode === 'schedule') {
      return `${currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} – ${new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Hour markers for day/week view
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Check if user has access to events/calendar (after all hooks)
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: colors.border, borderTopColor: colors.accent }} />
      </div>
    );
  }
  
  if (!hasFeature('events')) {
    return <UpgradePrompt feature="Calendar & Events" description="Manage your schedule with a full calendar view, create events, and sync with Google Calendar for seamless planning." />;
  }

  // Show skeleton while data is loading
  if (dataLoading) {
    return (
      <div className="min-h-screen px-4 pt-4" style={{ backgroundColor: 'transparent' }}>
        <EventsPageSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen md:min-h-0 md:h-[calc(100dvh-89px)] flex flex-col" style={{ backgroundColor: 'transparent' }}>
      {/* Google Calendar Reconnect Prompt */}
      {showReconnectPrompt && (
        <div 
          className="mx-4 mt-4 p-4 rounded-md flex items-center justify-between gap-4 "
          style={{ 
            backgroundColor: `${colors.accent}20`, 
            border: `1px solid ${colors.accent}40`,
          }}
        >
          <div className="flex items-center gap-3">
            <AlertTriangle size={20} style={{ color: colors.accent }} />
            <div>
              <p className="font-medium" style={{ color: colors.textPrimary }}>
                Google Calendar connection lost
              </p>
              <p className="text-sm" style={{ color: colors.textMuted }}>
                Your Google Calendar access was revoked. Reconnect to continue syncing.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={dismissReconnectPrompt}
              className="px-3 py-1.5 text-sm rounded-lg transition-colors notebook-row-hover"
              style={{ color: colors.textMuted }}
              disabled={isReconnecting}
            >
              Don't show again
            </button>
            <button
              onClick={reconnectGoogle}
              disabled={isReconnecting}
              className="px-4 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-2"
              style={{ 
                backgroundColor: colors.accent, 
                color: colors.onAccent,
                opacity: isReconnecting ? 0.7 : 1,
              }}
            >
              {isReconnecting ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Connecting...
                </>
              ) : (
                'Reconnect'
              )}
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-1 min-h-0">
      {/* Left Sidebar - Hidden on mobile, folds away on desktop */}
      <div 
        className={`hidden ${sidebarOpen ? 'md:flex' : ''} flex-shrink-0 w-64 border-r flex-col`}
        style={{ borderColor: colors.border, backgroundColor: 'transparent' }}
      >
        <div className="p-4 flex-1 overflow-y-auto">
          {/* Create Button */}
          <button
            onClick={() => openAddModal(selectedDate || new Date())}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg  transition-all   mb-2 ${isAtEventLimit ? 'opacity-75' : ''}`}
            style={{ 
              backgroundColor: colors.bg, 
              border: `1px solid ${colors.border}`,
              
            }}
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: colors.accent }}>
              <Plus size={20} color={colors.onAccent} />
            </div>
            <span style={{ color: colors.textPrimary }} className="font-medium">
              Create {isAtEventLimit && <span className="text-[10px] opacity-70">({events.length}/{eventLimit})</span>}
            </span>
          </button>
          {isAtEventLimit && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="w-full text-xs font-medium hover:underline transition-all mb-4 text-center"
              style={{ color: colors.accent }}
            >
              Upgrade for unlimited events →
            </button>
          )}
          {!isAtEventLimit && <div className="mb-4" />}

          {/* Mini Calendar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <div className="flex gap-1">
                <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))} 
                  className="p-1 rounded notebook-row-hover" style={{ color: colors.textMuted }}>
                  <ChevronLeft size={14} />
                </button>
                <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))} 
                  className="p-1 rounded notebook-row-hover" style={{ color: colors.textMuted }}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-7 gap-0.5 text-center">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <div key={i} className="text-[10px] py-1" style={{ color: colors.textMuted }}>{d}</div>
              ))}
              {miniCalendarDays.map((day, i) => (
                <button
                  key={i}
                  onClick={() => { setSelectedDate(day.date); if (viewMode !== 'month') setViewMode('day'); }}
                  className={`text-[11px] w-6 h-6 rounded-full flex items-center justify-center transition-all ${isToday(day.date) ? 'font-bold' : ''}`}
                  style={{ 
                    color: day.isCurrentMonth ? colors.textPrimary : colors.textMuted,
                    backgroundColor: isToday(day.date) ? colors.accent : (selectedDate && isSameDate(day.date, selectedDate) ? colors.bgTertiary : 'transparent'),
                  }}
                >
                  {day.date.getDate()}
                </button>
              ))}
            </div>
          </div>

          {/* My Calendars */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium" style={{ color: colors.textMuted }}>My Calendars</span>
              <button onClick={() => { setEditingCalendar(null); setCalendarForm({ name: '', color: CALENDAR_COLORS[0], syncToGoogle: false }); setShowAddCalendar(true); }}
                className="p-1 rounded notebook-row-hover" style={{ color: colors.textMuted }}>
                <Plus size={14} />
              </button>
            </div>
            
            <div className="space-y-3">
              {localCalendars.map(calendar => (
                <div key={calendar.id} className="flex items-center gap-3 px-2 py-1 rounded-lg group notebook-row-hover cursor-pointer"
                  onClick={() => toggleCalendarVisibility(calendar.id)}>
                  {/* 8px solid circular color dot */}
                  <div 
                    className="w-2 h-2 rounded-full flex-shrink-0 transition-opacity"
                    style={{ 
                      backgroundColor: calendar.color,
                      opacity: calendar.visible ? 1 : 0.3
                    }}
                  />
                  <span 
                    className="text-sm flex-1 truncate transition-opacity" 
                    style={{ 
                      color: colors.textPrimary,
                      opacity: calendar.visible ? 1 : 0.5
                    }}
                  >
                    {calendar.name}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); openCalendarEdit(calendar); }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded notebook-row-hover"
                    style={{ color: colors.textMuted }}
                  >
                    <MoreVertical size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Google Calendars */}
          {googleCalendarConnected && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium" style={{ color: colors.textMuted }}>Google Calendars</span>
                <button onClick={syncWithGoogleCalendar} disabled={syncing}
                  className={`p-1 rounded notebook-row-hover ${syncing ? 'animate-spin' : ''}`} style={{ color: colors.textMuted }}>
                  <RefreshCw size={14} />
                </button>
              </div>
              {googleCalendars.length === 0 ? (
                <div className="text-xs text-center py-2" style={{ color: colors.textMuted }}>
                  {syncing ? 'Syncing...' : 'Click refresh to sync calendars'}
                </div>
              ) : (
              <div className="space-y-3">
                {googleCalendars.filter(c => !c.name?.endsWith('- mind.')).map(calendar => {
                  const displayColor = getGoogleCalendarColor(calendar.id);
                  return (
                    <div key={calendar.id} className="flex items-center gap-3 px-2 py-1 rounded-lg group notebook-row-hover cursor-pointer"
                      onClick={() => toggleCalendarVisibility(calendar.id, true)}>
                      {/* 8px solid circular color dot */}
                      <div 
                        className="w-2 h-2 rounded-full flex-shrink-0 transition-opacity"
                        style={{ 
                          backgroundColor: displayColor,
                          opacity: calendar.visible ? 1 : 0.3
                        }}
                      />
                      <span 
                        className="text-sm flex-1 truncate transition-opacity" 
                        style={{ 
                          color: colors.textPrimary,
                          opacity: calendar.visible ? 1 : 0.5
                        }}
                      >
                        {calendar.name}
                      </span>
                      {calendar.syncError && (
                        <span className="font-mono text-[10px] flex-none" style={{ color: colors.textMuted }} title={calendar.syncError}>not synced</span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingGoogleCalendar({ ...calendar, displayColor }); }}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded notebook-row-hover"
                        style={{ color: colors.textMuted }}
                      >
                        <MoreVertical size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {/* Tasks */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium" style={{ color: colors.textMuted }}>Tasks</span>
            </div>
            <div className="flex items-center gap-3 px-2 py-1 rounded-lg group notebook-row-hover cursor-pointer"
              onClick={() => setTaskSettings(prev => ({ ...prev, visible: !prev.visible }))}>
              {/* 8px solid circular color dot */}
              <div 
                className="w-2 h-2 rounded-full flex-shrink-0 transition-opacity"
                style={{ 
                  backgroundColor: taskSettings.color,
                  opacity: taskSettings.visible ? 1 : 0.3
                }}
              />
              <CheckSquare size={14} style={{ color: taskSettings.color, opacity: taskSettings.visible ? 1 : 0.5 }} />
              <span 
                className="text-sm flex-1 transition-opacity" 
                style={{ 
                  color: colors.textPrimary,
                  opacity: taskSettings.visible ? 1 : 0.5
                }}
              >
                Tasks
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); setEditingSpecialCalendar('tasks'); }}
                className="opacity-0 group-hover:opacity-100 p-1 rounded notebook-row-hover"
                style={{ color: colors.textMuted }}
              >
                <MoreVertical size={12} />
              </button>
            </div>
          </div>

          {/* Goals */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium" style={{ color: colors.textMuted }}>Goals</span>
            </div>
            <div className="flex items-center gap-3 px-2 py-1 rounded-lg group notebook-row-hover cursor-pointer"
              onClick={() => setGoalSettings(prev => ({ ...prev, visible: !prev.visible }))}>
              {/* 8px solid circular color dot */}
              <div 
                className="w-2 h-2 rounded-full flex-shrink-0 transition-opacity"
                style={{ 
                  backgroundColor: goalSettings.color,
                  opacity: goalSettings.visible ? 1 : 0.3
                }}
              />
              <Target size={14} style={{ color: goalSettings.color, opacity: goalSettings.visible ? 1 : 0.5 }} />
              <span 
                className="text-sm flex-1 transition-opacity" 
                style={{ 
                  color: colors.textPrimary,
                  opacity: goalSettings.visible ? 1 : 0.5
                }}
              >
                Goals
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); setEditingSpecialCalendar('goals'); }}
                className="opacity-0 group-hover:opacity-100 p-1 rounded notebook-row-hover"
                style={{ color: colors.textMuted }}
              >
                <MoreVertical size={12} />
              </button>
            </div>
          </div>

          {/* Settings - Collapsed dropdown for Google connection */}
          <div className="mt-auto pt-4 border-t" style={{ borderColor: colors.border }}>
            <details className="group">
              <summary 
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs cursor-pointer notebook-row-hover list-none"
                style={{ color: colors.textMuted }}
              >
                <MoreVertical size={14} />
                <span>Settings</span>
                <ChevronRight size={12} className="ml-auto transition-transform group-open:rotate-90" />
              </summary>
              <div className="mt-2 pl-2">
                {!googleCalendarConnected ? (
                  <button 
                    onClick={() => {
                      if (canConnectGoogle) connectGoogleCalendar();
                    }}
                    disabled={isConnectingCalendar || !canConnectGoogle}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm notebook-row-hover disabled:opacity-50 disabled:cursor-not-allowed" 
                    style={{ color: canConnectGoogle ? colors.accent : colors.textMuted }}
                    title={!canConnectGoogle ? 'Upgrade to Pro to connect Google Calendar' : ''}
                  >
                    {isConnectingCalendar ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Plus size={14} /> Connect Google Calendar
                        {!canConnectGoogle && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: colors.accent, color: colors.onAccent }}>Pro</span>}
                      </>
                    )}
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 px-3 py-2 text-xs" style={{ color: colors.success }}>
                      <Check size={12} /> Google Calendar Connected
                    </div>
                    <button onClick={disconnectGoogleCalendar}
                      className="w-full px-3 py-2 rounded-lg text-xs notebook-row-hover" style={{ color: colors.textMuted }}>
                      Disconnect Google
                    </button>
                    <a 
                      href="https://calendar.google.com/calendar/r/settings" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block px-3 py-2 text-[10px] leading-relaxed hover:underline"
                      style={{ color: colors.textMuted }}
                    >
                      Open Google Calendar Settings →
                    </a>
                  </div>
                )}
              </div>
            </details>
          </div>
        </div>
      </div>

      {/* Main Calendar Area - Desktop */}
      <div className="hidden md:flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b animate-fade-in animate-delay-2" style={{ borderBottomColor: colors.textPrimary }}>
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={toggleSidebar}
              className="p-1.5 notebook-row-hover"
              style={{ color: colors.textSecondary }}
              aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
              aria-expanded={sidebarOpen}
              title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              {sidebarOpen ? <PanelLeftClose size={18} strokeWidth={1.5} /> : <PanelLeftOpen size={18} strokeWidth={1.5} />}
            </button>
            <h1
              className="text-[24px] font-semibold leading-none"
              style={{ color: colors.textPrimary, letterSpacing: '-0.035em' }}
              title={`Events, tasks and goals${googleCalendarConnected ? ' · synced with Google Calendar' : ''}`}
            >
              Calendar
            </h1>
            <button onClick={goToToday}
              className="px-4 py-1.5 text-sm rounded-lg border notebook-row-hover"
              style={{ borderColor: colors.border, color: colors.textPrimary }}>
              Today
            </button>
            <div className="flex items-center gap-1">
              <button onClick={() => navigate(-1)} className="p-2 rounded-full notebook-row-hover" style={{ color: colors.textPrimary }}>
                <ChevronLeft size={20} />
              </button>
              <button onClick={() => navigate(1)} className="p-2 rounded-full notebook-row-hover" style={{ color: colors.textPrimary }}>
                <ChevronRight size={20} />
              </button>
            </div>
            <h2 className="text-xl font-medium" style={{ color: colors.textPrimary }}>{getHeaderText()}</h2>
          </div>

          {/* View Switcher - Responsive: dropdown on tablet, buttons on desktop */}
          {/* Custom dropdown for tablets (hidden on large screens) */}
          <div className="lg:hidden relative">
            <button
              onClick={() => setShowViewDropdown(!showViewDropdown)}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-md cursor-pointer font-medium"
              style={{ 
                backgroundColor: colors.bgSecondary, 
                border: `1px solid ${colors.border}`,
                
                color: colors.textPrimary,
              }}
            >
              {viewMode}
              <ChevronDown size={16} style={{ color: colors.textMuted }} />
            </button>
            
            {/* Dropdown menu */}
            {showViewDropdown && (
              <>
                {/* Backdrop to close dropdown */}
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowViewDropdown(false)}
                />
                <div 
                  className="absolute right-0 top-full mt-2 z-50 rounded-md overflow-hidden "
                  style={{ 
                    backgroundColor: colors.bgSecondary, 
                    border: `1px solid ${colors.border}`,
                    minWidth: '140px'
                  }}
                >
                  {[
                    { id: 'schedule', label: 'Schedule' },
                    { id: 'day', label: 'Day' },
                    { id: 'week', label: 'Week' },
                    { id: 'month', label: 'Month' },
                  ].map(({ id, label }) => (
                    <button
                      key={id}
                      onClick={() => {
                        setViewMode(id);
                        if (id === 'day' && !selectedDate) setSelectedDate(new Date());
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('view', id);
                        setSearchParams(newParams, { replace: true });
                        setShowViewDropdown(false);
                      }}
                      className="w-full px-4 py-3 text-sm text-left flex items-center justify-between notebook-row-hover"
                      style={{ 
                        color: viewMode === id ? colors.accent : colors.textPrimary,
                        backgroundColor: viewMode === id ? `${colors.accent}15` : 'transparent',
                      }}
                    >
                      {label}
                      {viewMode === id && <Check size={16} style={{ color: colors.accent }} />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Button group for desktop (hidden on tablet) */}
          <div className="hidden lg:flex rounded-md overflow-hidden" style={{ 
            backgroundColor: colors.bgSecondary, 
            border: `1px solid ${colors.border}`,
            
          }}>
            {[
              { id: 'schedule', icon: List, label: 'Schedule' },
              { id: 'day', icon: null, label: 'Day' },
              { id: 'week', icon: null, label: 'Week' },
              { id: 'month', icon: Grid, label: 'Month' },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => { 
                  setViewMode(id); 
                  if (id === 'day' && !selectedDate) setSelectedDate(new Date());
                  // Update URL with view parameter
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('view', id);
                  setSearchParams(newParams, { replace: true });
                }}
                className="px-4 py-2 text-xs transition-all font-medium"
                style={{ backgroundColor: viewMode === id ? colors.accent : 'transparent', color: viewMode === id ? colors.onAccent : colors.textMuted }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Calendar Content */}
        <div className="flex-1 min-h-0 overflow-auto p-4">
          
          {/* ==================== MONTH VIEW ==================== */}
          {viewMode === 'month' && (
            <div className="h-full flex flex-col rounded-lg overflow-hidden" style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}>
              {/* Day headers */}
              <div className="grid grid-cols-7" style={{ borderBottom: `1px solid ${colors.border}` }}>
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                  <div key={day} className="px-2 py-3 text-xs tracking-wider text-center font-medium" style={{ color: colors.textMuted }}>{day}</div>
                ))}
              </div>
              
              {/* Calendar grid - render by week rows for multi-day spanning */}
              <div className="flex-1 flex flex-col">
                {[0, 1, 2, 3, 4, 5].map(weekIndex => {
                  const weekDays = calendarDays.slice(weekIndex * 7, weekIndex * 7 + 7);
                  
                  // Get all events for this week (for multi-day span calculation)
                  const allWeekEvents = [];
                  weekDays.forEach(day => {
                    const dayEvents = getEventsForDate(day.date);
                    dayEvents.forEach(e => {
                      if (!allWeekEvents.find(existing => (existing.id || existing.googleEventId) === (e.id || e.googleEventId))) {
                        allWeekEvents.push(e);
                      }
                    });
                  });
                  
                  // Calculate multi-day event spans for this week
                  const multiDaySpans = calculateMultiDaySpans(weekDays, allWeekEvents, formatDateString);
                  const maxSpanRow = multiDaySpans.length > 0 ? Math.max(...multiDaySpans.map(s => s.row)) : -1;
                  const spanRowHeight = 18; // Height per multi-day event row
                  
                  return (
                    <div key={weekIndex} className="flex-1 flex flex-col min-h-[100px]" style={{ backgroundColor: colors.bgSecondary, borderTop: `1px solid ${colors.border}` }}>
                      {/* Date numbers row - always at the top */}
                      <div className="grid grid-cols-7" style={{ backgroundColor: colors.bgSecondary }}>
                        {weekDays.map((day, colIdx) => {
                          const isCurrentDay = isToday(day.date);
                          return (
                            <div
                              key={colIdx}
                              onClick={() => { setSelectedDate(day.date); setViewMode('day'); }}
                              className="p-1 cursor-pointer"
                              style={{ backgroundColor: isCurrentDay ? `${colors.accent}15` : colors.bgSecondary, borderRight: colIdx < 6 ? `1px solid ${colors.border}` : 'none' }}
                            >
                              <div className="flex justify-end">
                                <span className={`text-sm w-6 h-6 flex items-center justify-center rounded-full transition-all ${isCurrentDay ? 'font-semibold' : ''}`}
                                  style={{ backgroundColor: isCurrentDay ? colors.accent : 'transparent', color: isCurrentDay ? colors.onAccent : day.isCurrentMonth ? colors.textPrimary : colors.textMuted }}>
                                  {day.date.getDate()}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Multi-day spanning events area - below dates */}
                      {multiDaySpans.length > 0 && (
                        <div className="relative grid grid-cols-7" style={{ height: `${(maxSpanRow + 1) * spanRowHeight + 2}px` }}>
                          {/* Grid column backgrounds with borders */}
                          {weekDays.map((day, colIdx) => {
                            const isCurrentDay = isToday(day.date);
                            return (
                              <div 
                                key={colIdx} 
                                style={{ 
                                  backgroundColor: isCurrentDay ? `${colors.accent}15` : colors.bgSecondary,
                                  borderRight: colIdx < 6 ? `1px solid ${colors.border}` : 'none'
                                }} 
                              />
                            );
                          })}
                          {/* Spanning event bars overlaid on top */}
                          {multiDaySpans.map((span, spanIdx) => {
                            const leftPercent = (span.startCol / 7) * 100;
                            const widthPercent = (span.spanCols / 7) * 100;
                            const eventColor = getEventColorWithCalendars(span.event);
                            
                            return (
                              <div
                                key={span.event.id || span.event.googleEventId || spanIdx}
                                onClick={(e) => { e.stopPropagation(); openPreviewEvent(span.event); }}
                                className="absolute cursor-pointer hover:brightness-110 transition-all flex items-center overflow-hidden"
                                style={{
                                  left: `calc(${leftPercent}% + 2px)`,
                                  width: `calc(${widthPercent}% - 4px)`,
                                  top: `${span.row * spanRowHeight + 1}px`,
                                  height: `${spanRowHeight - 2}px`,
                                  backgroundColor: colors.bg,
                                  border: itemRule(eventColor),
                                  borderLeftStyle: span.isStart ? 'solid' : 'dashed',
                                  borderRightStyle: span.isEnd ? 'solid' : 'dashed',
                                  paddingLeft: span.isStart ? '6px' : '2px',
                                  paddingRight: span.isEnd ? '6px' : '2px',
                                }}
                              >
                                <span className="text-[10px] truncate font-semibold" style={{ color: colors.textPrimary }}>
                                  {span.event.name || span.event.title}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Single-day events row - at the bottom */}
                      <div className="flex-1 grid grid-cols-7" style={{ minHeight: '90px', backgroundColor: colors.bgSecondary }}>
                        {weekDays.map((day, colIdx) => {
                          const isCurrentDay = isToday(day.date);
                          
                          // Get single-day events only (multi-day already rendered above)
                          const dayEvents = getEventsForDate(day.date).filter(e => {
                            const startDate = e.startDate || e.date;
                            const endDate = e.endDate || e.date || startDate;
                            return startDate === endDate; // Single-day only
                          });
                          const dayTasks = taskSettings.visible ? getTasksForDate(day.date) : [];
                          const dayGoals = goalSettings.visible ? getGoalsForDate(day.date) : [];
                          
                          const singleDayItems = [
                            ...dayEvents.map(e => ({ ...e, itemType: 'event' })),
                            ...dayTasks.map(t => ({ ...t, itemType: 'task', name: t.title })),
                            ...dayGoals.map(g => ({ ...g, itemType: 'goal', name: g.title }))
                          ];

                          // Show up to 6 items per day cell
                          const maxItemsToShow = 6;
                          
                          return (
                            <div
                              key={colIdx}
                              onClick={() => { setSelectedDate(day.date); setViewMode('day'); }}
                              className="px-1 pb-1 cursor-pointer notebook-row-hover transition-all flex flex-col"
                              style={{ backgroundColor: isCurrentDay ? `${colors.accent}15` : colors.bgSecondary, borderRight: colIdx < 6 ? `1px solid ${colors.border}` : 'none' }}
                            >
                            <div className="space-y-0.5 overflow-hidden flex-1">
                                {singleDayItems.slice(0, maxItemsToShow).map((item, idx) => {
                                  const itemColor = item.itemType === 'task' ? taskSettings.color : item.itemType === 'goal' ? goalSettings.color : getEventColorWithCalendars(item);
                                  return (
                                    <div key={item.id || idx}
                                      onClick={(e) => { e.stopPropagation(); item.itemType === 'event' ? openPreviewEvent(item) : item.itemType === 'task' ? openPreviewTask(item) : item.itemType === 'goal' && openPreviewGoal(item); }}
                                      className="text-[10px] leading-tight px-1.5 py-0.5 cursor-pointer hover:brightness-110 transition-all flex items-center gap-1 overflow-hidden"
                                      style={{ 
                                        backgroundColor: colors.bg,
                                        border: itemRule(itemColor),
                                      }}>
                                      {item.itemType === 'task' && <CheckSquare size={8} className="flex-shrink-0" style={{ color: itemColor }} />}
                                      {item.itemType === 'goal' && <Target size={8} className="flex-shrink-0" style={{ color: itemColor }} />}
                                      {item.itemType === 'event' && item.startTime && <span className="mr-0.5 font-medium" style={{ color: colors.textSecondary }}>{formatTimeShort(item.startTime)}</span>}
                                      <span className="truncate font-medium" style={{ color: colors.textPrimary }}>{item.name}</span>
                                    </div>
                                  );
                                })}
                                {singleDayItems.length > maxItemsToShow && (
                                  <div className="text-[9px] px-1" style={{ color: colors.textMuted }}>+{singleDayItems.length - maxItemsToShow} more</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ==================== WEEK VIEW ==================== */}
          {viewMode === 'week' && (
            <div className="h-full flex flex-col rounded-lg overflow-hidden" style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}>
              {/* Week Header - Day names and dates */}
              <div className="flex" style={{ borderBottom: `1px solid ${colors.border}`, scrollbarGutter: 'stable', overflow: 'hidden' }}>
                <div className="w-16 flex-shrink-0" /> {/* Time gutter */}
                {weekDays.map((day, i) => {
                  const isCurrentDay = isToday(day);
                  return (
                    <div key={i} className="flex-1 text-center py-3" style={{ borderLeft: `1px solid ${colors.border}`, minWidth: '100px' }}>
                      <div className="text-xs font-medium" style={{ color: colors.textMuted }}>
                        {day.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div 
                        className={`text-xl mt-1 w-10 h-10 mx-auto flex items-center justify-center rounded-full transition-all ${isCurrentDay ? 'font-semibold' : ''}`}
                        style={{ backgroundColor: isCurrentDay ? colors.accent : 'transparent', color: isCurrentDay ? colors.onAccent : colors.textPrimary }}
                      >
                        {day.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* All-day events row with spanning bars for multi-day events */}
              {(() => {
                // Collect all multi-day events for the week (both all-day and timed multi-day)
                const allWeekEvents = [];
                weekDays.forEach(day => {
                  // Get ALL events for the day (both all-day and timed)
                  const dayEvents = getEventsForDate(day);
                  dayEvents.forEach(e => {
                    // Include if it's a multi-day event (all-day OR timed spanning multiple days)
                    const startDate = e.startDate || e.date;
                    const endDate = e.endDate || e.date || startDate;
                    const isMultiDay = startDate && endDate && startDate !== endDate;
                    
                    if (isMultiDay && !allWeekEvents.find(existing => (existing.id || existing.googleEventId) === (e.id || e.googleEventId))) {
                      allWeekEvents.push(e);
                    }
                  });
                  
                  // Also get all-day single-day events
                  const allDayEvents = getAllDayEvents(day);
                  allDayEvents.forEach(e => {
                    if (!allWeekEvents.find(existing => (existing.id || existing.googleEventId) === (e.id || e.googleEventId))) {
                      // Only add single-day all-day events (multi-day already added above)
                      const startDate = e.startDate || e.date;
                      const endDate = e.endDate || e.date || startDate;
                      if (startDate === endDate) {
                        // This will be handled in singleDayItemsByCol below
                      }
                    }
                  });
                });
                
                // Calculate multi-day spans
                const multiDaySpans = calculateMultiDaySpans(
                  weekDays.map(d => ({ date: d })), 
                  allWeekEvents, 
                  formatDateString
                );
                
                // Get single-day all-day events per day
                const singleDayItemsByCol = weekDays.map(day => {
                  const allDayEvents = getAllDayEvents(day).filter(e => {
                    const startDate = e.startDate || e.date;
                    const endDate = e.endDate || e.date || startDate;
                    return startDate === endDate;
                  });
                  const dayTasks = taskSettings.visible ? getTasksForDate(day) : [];
                  const dayGoals = goalSettings.visible ? getGoalsForDate(day) : [];
                  return [
                    ...allDayEvents.map(e => ({ ...e, itemType: 'event' })),
                    ...dayTasks.map(t => ({ ...t, itemType: 'task', name: t.title })),
                    ...dayGoals.map(g => ({ ...g, itemType: 'goal', name: g.title }))
                  ];
                });
                
                const hasAnyItems = multiDaySpans.length > 0 || singleDayItemsByCol.some(items => items.length > 0);
                if (!hasAnyItems) return null;
                
                const maxSpanRow = multiDaySpans.length > 0 ? Math.max(...multiDaySpans.map(s => s.row)) : -1;
                const spanRowHeight = 22;
                const multiDayHeight = multiDaySpans.length > 0 ? (maxSpanRow + 1) * spanRowHeight + 4 : 0;
                const singleDayMaxItems = 2;
                const hasSingleDay = singleDayItemsByCol.some(items => items.length > 0);
                
                return (
                  <div style={{ borderBottom: `1px solid ${colors.border}` }}>
                    {/* Multi-day spanning events */}
                    {multiDaySpans.length > 0 && (
                      <div className="flex" style={{ scrollbarGutter: 'stable', overflow: 'hidden' }}>
                        <div className="w-16 flex-shrink-0 text-[10px] text-right pr-2 py-2 font-medium" style={{ color: colors.textMuted }}>all-day</div>
                        <div className="flex-1 relative" style={{ height: `${multiDayHeight}px` }}>
                          {/* Column backgrounds with borders - clickable to add all-day events */}
                          <div className="absolute inset-0 flex">
                            {weekDays.map((day, colIdx) => (
                              <div 
                                key={colIdx} 
                                className="flex-1 cursor-pointer notebook-row-hover transition-colors" 
                                style={{ backgroundColor: colors.bgSecondary, borderRight: colIdx < 6 ? `1px solid ${colors.border}` : 'none', minWidth: '100px' }}
                                onClick={() => openAddModal(day, null, true)}
                              />
                            ))}
                          </div>
                          {/* Spanning bars */}
                          {multiDaySpans.map((span, spanIdx) => {
                            const leftPercent = (span.startCol / 7) * 100;
                            const widthPercent = (span.spanCols / 7) * 100;
                            const eventColor = getEventColorWithCalendars(span.event);
                            
                            // For timed multi-day events, show times in the label
                            const isTimedMultiDay = span.event.isMultiDayTimed || (span.event.startTime && span.event.endDate);
                            let label = span.event.name || span.event.title;
                            if (isTimedMultiDay && span.event.startTime) {
                              const startTimeFormatted = formatTimeShort(span.event.startTime);
                              const endTimeFormatted = span.event.endTime ? formatTimeShort(span.event.endTime) : '';
                              label = `${startTimeFormatted} - ${endTimeFormatted}, ${label}`;
                            }
                            
                            return (
                              <div
                                key={span.event.id || span.event.googleEventId || spanIdx}
                                onClick={(e) => { e.stopPropagation(); openPreviewEvent(span.event); }}
                                className="absolute cursor-pointer hover:brightness-110 transition-all flex items-center overflow-hidden"
                                style={{
                                  left: `calc(${leftPercent}% + 4px)`,
                                  width: `calc(${widthPercent}% - 8px)`,
                                  top: `${span.row * spanRowHeight + 2}px`,
                                  height: `${spanRowHeight - 4}px`,
                                  backgroundColor: colors.bg,
                                  border: itemRule(eventColor),
                                  borderLeftStyle: span.isStart ? 'solid' : 'dashed',
                                  borderRightStyle: span.isEnd ? 'solid' : 'dashed',
                                  paddingLeft: span.isStart ? '8px' : '4px',
                                  paddingRight: span.isEnd ? '8px' : '4px',
                                }}
                              >
                                <span className="text-[11px] truncate font-semibold" style={{ color: colors.textPrimary }}>
                                  {label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Single-day all-day events, tasks, goals */}
                    {hasSingleDay && (
                      <div className="flex" style={{ scrollbarGutter: 'stable', overflow: 'hidden' }}>
                        <div className="w-16 flex-shrink-0 text-[10px] text-right pr-2 py-1 font-medium" style={{ color: colors.textMuted }}>
                          {multiDaySpans.length === 0 ? 'all-day' : ''}
                        </div>
                        {weekDays.map((day, i) => {
                          const items = singleDayItemsByCol[i];
                          return (
                            <div 
                              key={i} 
                              className="flex-1 p-1 min-h-[28px] overflow-hidden cursor-pointer notebook-row-hover transition-colors" 
                              style={{ borderLeft: `1px solid ${colors.border}`, minWidth: '100px' }}
                              onClick={() => openAddModal(day, null, true)}
                            >
                              {items.slice(0, singleDayMaxItems).map((item, idx) => {
                                const itemColor = item.itemType === 'task' ? taskSettings.color : item.itemType === 'goal' ? goalSettings.color : getEventColorWithCalendars(item);
                                return (
                                  <div key={item.id || idx}
                                    onClick={(e) => { e.stopPropagation(); item.itemType === 'event' ? openPreviewEvent(item) : item.itemType === 'task' ? openPreviewTask(item) : item.itemType === 'goal' && openPreviewGoal(item); }}
                                    className="text-[11px] px-2 py-1 mb-0.5 cursor-pointer flex items-center gap-1.5 overflow-hidden hover:brightness-110 transition-all"
                                    style={{ 
                                      backgroundColor: colors.bg,
                                      border: itemRule(itemColor),
                                    }}>
                                    {item.itemType === 'task' && <CheckSquare size={10} className="flex-shrink-0" style={{ color: itemColor }} />}
                                    {item.itemType === 'goal' && <Target size={10} className="flex-shrink-0" style={{ color: itemColor }} />}
                                    <span className="truncate font-medium" style={{ color: colors.textPrimary }}>{item.name}</span>
                                  </div>
                                );
                              })}
                              {items.length > singleDayMaxItems && (
                                <div className="text-[9px] px-1" style={{ color: colors.textMuted }}>+{items.length - singleDayMaxItems} more</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Time Grid - constrained height with scroll */}
              <div ref={weekScrollRef} className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
                <div className="flex relative" style={{ height: `${24 * 60}px`, paddingBottom: '8px' }}>
                  {/* Time Labels - right-aligned, flush against grid */}
                  <div className="w-16 flex-shrink-0 relative">
                    {hours.map(hour => (
                      <div key={hour} className="absolute right-0 text-right pr-1" 
                        style={{ 
                          top: `${hour * 60 - 7}px`, 
                          color: colors.textMuted,
                          fontSize: '10px',
                          fontWeight: 500,
                          letterSpacing: '0.02em'
                        }}>
                        {hour === 0 ? '' : hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                      </div>
                    ))}
                  </div>
                  
                  {/* Day Columns */}
                  {weekDays.map((day, dayIndex) => {
                    // Filter out multi-day timed events (they're shown in the spanning bar area)
                    const timedEvents = getTimedEvents(day).filter(e => {
                      const startDate = e.startDate || e.date;
                      const endDate = e.endDate || e.date || startDate;
                      const isMultiDay = startDate && endDate && startDate !== endDate;
                      return !isMultiDay; // Only show single-day timed events in the time grid
                    });
                    return (
                      <div key={dayIndex} className="flex-1 border-l relative" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', minWidth: '100px' }}>
                        {/* Hour lines - subtle grid */}
                        {hours.map(hour => (
                          <div key={hour} className="absolute left-0 right-0" 
                            style={{ top: `${hour * 60}px`, height: '1px', backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                        ))}
                        
                        {/* Current time indicator - Linear blue line */}
                        {isToday(day) && (
                          <div className="absolute left-0 right-0 flex items-center z-10 pointer-events-none" 
                            style={{ top: `${new Date().getHours() * 60 + new Date().getMinutes()}px` }}>
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.textPrimary }} />
                            <div className="flex-1" style={{ height: '1px', backgroundColor: colors.textPrimary }} />
                          </div>
                        )}
                        
                        {/* Events - Google-style stacked glass cards */}
                        {calculateEventLayout(timedEvents).map(event => {
                          const top = event.startMin;
                          const height = Math.max(24, event.endMin - event.startMin);
                          const eventColor = getEventColorWithCalendars(event);
                          // Google-style stacking: indent based on stack level
                          const indentPx = event.indentPx || 0;
                          // Store timeout ID on element for cleanup
                          let hoverTimeout = null;
                          
                          return (
                            <div
                              key={event.id}
                              onClick={(e) => { e.stopPropagation(); openPreviewEvent(event); }}
                              className="absolute cursor-pointer overflow-hidden group"
                              style={{
                                top: `${top}px`,
                                height: `${height}px`,
                                left: `${indentPx}px`,
                                right: '2px',
                                zIndex: 20 + (event.stackLevel || 0),
                                // Solid paper keeps overlapping events from showing through each other
                                backgroundColor: colors.bg,
                                border: itemRule(eventColor),
                                // Reduced internal padding
                                padding: `${EVENT_PADDING_V}px ${EVENT_PADDING_H}px`,
                                // Add depth shadow for layered events
                                boxShadow: 'none',
                                // Smooth transitions
                                transition: 'transform 0.15s ease, z-index 0s, box-shadow 0.15s ease, background-color 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                const target = e.currentTarget;
                                // 300ms delay before hover effect activates
                                hoverTimeout = setTimeout(() => {
                                  target.style.zIndex = '100';
                                  target.style.backgroundColor = itemTint(eventColor);
                                }, 300);
                              }}
                              onMouseLeave={(e) => {
                                // Clear the timeout if mouse leaves before delay completes
                                if (hoverTimeout) clearTimeout(hoverTimeout);
                                e.currentTarget.style.zIndex = String(20 + (event.stackLevel || 0));
                                e.currentTarget.style.backgroundColor = colors.bg;
                              }}
                            >
                              {/* Title: 500 weight, 11px, adaptive color */}
                              <div 
                                className="truncate" 
                                style={{ 
                                  color: colors.textPrimary, 
                                  fontWeight: 500, 
                                  fontSize: '11px',
                                  lineHeight: '1.2',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {event.name}
                              </div>
                              {height > 28 && (
                                // Time: 400 weight, 10px, adaptive color
                                <div 
                                  style={{ 
                                    color: colors.textSecondary, 
                                    fontWeight: 400, 
                                    fontSize: '10px',
                                    marginTop: '1px'
                                  }}
                                >
                                  {formatTimeShort(event.startTime)} – {formatTimeShort(event.endTime)}
                                </div>
                              )}
                              {height > 45 && event.location && (
                                <div 
                                  className="truncate"
                                  style={{ 
                                    color: colors.textMuted, 
                                    fontWeight: 400, 
                                    fontSize: '10px',
                                    marginTop: '1px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {event.location}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Drag-to-create overlay */}
                        {dragCreate && dragCreate.date && formatDateString(dragCreate.date) === formatDateString(day) && (() => {
                          const startTotal = dragCreate.startHour * 60 + dragCreate.startMinutes;
                          const endTotal = dragCreate.currentHour * 60 + dragCreate.currentMinutes;
                          const top = Math.min(startTotal, endTotal);
                          const height = Math.max(15, Math.abs(endTotal - startTotal));
                          return (
                            <div
                              className="absolute left-1 right-1 rounded-lg pointer-events-none"
                              style={{
                                top: `${top}px`,
                                height: `${height}px`,
                                backgroundColor: `${colors.accent}40`,
                                border: `2px dashed ${colors.accent}`,
                                zIndex: 50
                              }}
                            />
                          );
                        })()}
                        
                        {/* Click/drag to add */}
                        <div 
                          className="absolute inset-0 cursor-crosshair"
                          style={{ zIndex: 5 }}
                          onMouseDown={(e) => {
                            const target = e.currentTarget;
                            const rect = target.getBoundingClientRect();
                            const y = e.clientY - rect.top;
                            const hour = Math.floor(y / 60);
                            const minutes = Math.floor((y % 60) / 15) * 15;
                            devLog('[DragCreate] onMouseDown - y:', y, 'hour:', hour, 'minutes:', minutes);
                            
                            if (isAtEventLimit) {
                              setShowUpgradeModal(true);
                              return;
                            }
                            
                            dragCreateRef.current = target;
                            const newDragCreate = {
                              date: day,
                              startHour: hour,
                              startMinutes: minutes,
                              currentHour: hour,
                              currentMinutes: minutes + 15
                            };
                            devLog('[DragCreate] onMouseDown - newDragCreate:', newDragCreate);
                            dragCreateDataRef.current = newDragCreate;
                            setDragCreate(newDragCreate);
                          }}
                          onTouchStart={(e) => {
                            const target = e.currentTarget;
                            const rect = target.getBoundingClientRect();
                            const y = e.touches[0].clientY - rect.top;
                            const hour = Math.floor(y / 60);
                            const minutes = Math.floor((y % 60) / 15) * 15;
                            
                            if (isAtEventLimit) {
                              setShowUpgradeModal(true);
                              return;
                            }
                            
                            dragCreateRef.current = target;
                            const newDragCreate = {
                              date: day,
                              startHour: hour,
                              startMinutes: minutes,
                              currentHour: hour,
                              currentMinutes: minutes + 15
                            };
                            dragCreateDataRef.current = newDragCreate;
                            setDragCreate(newDragCreate);
                          }}
                          onClick={(e) => {
                            // Only trigger if not a drag operation and didn't just finish a drag
                            if (dragCreate || justFinishedDragRef.current) return;
                            const rect = e.currentTarget.getBoundingClientRect();
                            const y = e.clientY - rect.top;
                            const hour = Math.floor(y / 60);
                            const minutes = Math.floor((y % 60) / 15) * 15;
                            openAddModal(day, `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ==================== DAY VIEW ==================== */}
          {viewMode === 'day' && selectedDate && (() => {
            // Check for multi-day events that include this day
            const dayEvents = getEventsForDate(selectedDate);
            const multiDayEvents = dayEvents.filter(e => {
              const startDate = e.startDate || e.date;
              const endDate = e.endDate || e.date || startDate;
              return startDate && endDate && startDate !== endDate;
            });
            
            // Single-day all-day events
            const singleDayAllDayEvents = getAllDayEvents(selectedDate).filter(e => {
              const startDate = e.startDate || e.date;
              const endDate = e.endDate || e.date || startDate;
              return startDate === endDate;
            });
            
            const dayTasks = taskSettings.visible ? getTasksForDate(selectedDate) : [];
            const dayGoals = goalSettings.visible ? getGoalsForDate(selectedDate) : [];
            const allDayItems = [
              ...singleDayAllDayEvents.map(e => ({ ...e, itemType: 'event' })),
              ...dayTasks.map(t => ({ ...t, itemType: 'task', name: t.title })),
              ...dayGoals.map(g => ({ ...g, itemType: 'goal', name: g.title }))
            ];
            
            // Get timed events for time grid (excluding multi-day timed events)
            const timedEventsForGrid = getTimedEvents(selectedDate).filter(e => {
              const startDate = e.startDate || e.date;
              const endDate = e.endDate || e.date || startDate;
              const isMultiDay = startDate && endDate && startDate !== endDate;
              return !isMultiDay;
            });
            
            return (
            <div className="h-full flex flex-col rounded-lg overflow-hidden" style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}>
              {/* Day Header */}
              <div className="flex px-4 py-4" style={{ borderBottom: `1px solid ${colors.border}` }}>
                <div className="w-16 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-medium" style={{ color: colors.textMuted }}>
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}
                  </div>
                  <div 
                    className={`text-2xl mt-1 w-12 h-12 flex items-center justify-center rounded-full transition-all ${isToday(selectedDate) ? 'font-semibold' : ''}`}
                    style={{ backgroundColor: isToday(selectedDate) ? colors.accent : 'transparent', color: isToday(selectedDate) ? colors.onAccent : colors.textPrimary }}
                  >
                    {selectedDate.getDate()}
                  </div>
                  
                  {/* Multi-day events (shown as full-width bars) */}
                  {multiDayEvents.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {multiDayEvents.map((event, idx) => {
                        const eventColor = getEventColorWithCalendars(event);
                        const isTimedMultiDay = event.isMultiDayTimed || (event.startTime && event.endDate);
                        let label = event.name || event.title;
                        if (isTimedMultiDay && event.startTime) {
                          label = `${formatTimeShort(event.startTime)} - ${formatTimeShort(event.endTime)}, ${label}`;
                        }
                        return (
                          <div key={event.id || idx}
                            onClick={() => openPreviewEvent(event)}
                            className="text-[11px] px-2 py-1 cursor-pointer flex items-center gap-1.5 overflow-hidden hover:brightness-110 transition-all"
                            style={{ 
                              backgroundColor: colors.bg,
                              border: itemRule(eventColor),
                            }}>
                            <span className="truncate font-medium" style={{ color: colors.textPrimary }}>{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* All-day events, tasks, goals */}
                  {allDayItems.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {allDayItems.map((item, idx) => {
                        const itemColor = item.itemType === 'task' ? taskSettings.color : item.itemType === 'goal' ? goalSettings.color : getEventColorWithCalendars(item);
                        return (
                          <div key={item.id || idx}
                            onClick={() => item.itemType === 'event' ? openPreviewEvent(item) : item.itemType === 'task' ? openPreviewTask(item) : item.itemType === 'goal' && openPreviewGoal(item)}
                            className="text-[11px] px-2 py-1 cursor-pointer flex items-center gap-1.5 overflow-hidden hover:brightness-110 transition-all"
                            style={{ 
                              backgroundColor: colors.bg,
                              border: itemRule(itemColor),
                            }}>
                            {item.itemType === 'task' && <CheckSquare size={10} className="flex-shrink-0" style={{ color: itemColor }} />}
                            {item.itemType === 'goal' && <Target size={10} className="flex-shrink-0" style={{ color: itemColor }} />}
                            <span className="truncate font-medium" style={{ color: colors.textPrimary }}>{item.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Time Grid - constrained height with scroll */}
              <div ref={dayScrollRef} className="flex-1 min-h-0 overflow-y-scroll">
                <div className="flex relative" style={{ height: `${24 * 60}px` }}>
                  {/* Time Labels - right-aligned, flush against grid */}
                  <div className="w-16 flex-shrink-0 relative">
                    {hours.map(hour => (
                      <div key={hour} className="absolute right-0 text-right pr-1" 
                        style={{ 
                          top: `${hour * 60 - 7}px`, 
                          color: colors.textMuted,
                          fontSize: '10px',
                          fontWeight: 500,
                          letterSpacing: '0.02em'
                        }}>
                        {hour === 0 ? '' : hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                      </div>
                    ))}
                  </div>
                  
                  {/* Day Column */}
                  <div className="flex-1 border-l relative" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
                    {/* Hour lines - subtle grid */}
                    {hours.map(hour => (
                      <div key={hour} className="absolute left-0 right-0" 
                        style={{ top: `${hour * 60}px`, height: '1px', backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                    ))}
                    
                    {/* Current time indicator - Linear blue line */}
                    {isToday(selectedDate) && (
                      <div className="absolute left-0 right-0 flex items-center z-10 pointer-events-none" 
                        style={{ top: `${new Date().getHours() * 60 + new Date().getMinutes()}px` }}>
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.textPrimary }} />
                        <div className="flex-1" style={{ height: '1px', backgroundColor: colors.textPrimary }} />
                      </div>
                    )}
                    
                    {/* Events (single-day timed only) - Two-column side-by-side layout for day view */}
                    {calculateTwoColumnLayout(timedEventsForGrid).map(event => {
                      const top = event.startMin;
                      const height = Math.max(24, event.endMin - event.startMin);
                      const eventColor = getEventColorWithCalendars(event);
                      // Two-column layout: use percentage-based positioning
                      const leftPercent = event.leftPercent || 0;
                      const widthPercent = event.widthPercent || 100;
                      
                      return (
                        <div
                          key={event.id}
                          onClick={(e) => { e.stopPropagation(); openPreviewEvent(event); }}
                          className="absolute cursor-pointer overflow-hidden transition-all hover:brightness-110"
                          style={{
                            top: `${top}px`,
                            height: `${height}px`,
                            left: `calc(${leftPercent}% + 2px)`,
                            width: `calc(${widthPercent}% - 4px)`,
                            zIndex: 20,
                            // Solid paper keeps overlapping events from showing through each other
                            backgroundColor: colors.bg,
                            border: itemRule(eventColor),
                            // Reduced internal padding
                            padding: `${EVENT_PADDING_V}px ${EVENT_PADDING_H}px`,
                          }}
                        >
                          {/* Title: 500 weight, 11px, adaptive color */}
                          <div 
                            className="truncate" 
                            style={{ 
                              color: colors.textPrimary, 
                              fontWeight: 500, 
                              fontSize: '11px',
                              lineHeight: '1.2',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {event.name}
                          </div>
                          {height > 28 && (
                            // Time: 400 weight, 10px, adaptive color
                            <div 
                              style={{ 
                                color: colors.textSecondary, 
                                fontWeight: 400, 
                                fontSize: '10px',
                                marginTop: '1px'
                              }}
                            >
                              {formatTimeShort(event.startTime)} – {formatTimeShort(event.endTime)}
                            </div>
                          )}
                          {height > 45 && event.location && (
                            <div 
                              className="flex items-center gap-1 truncate"
                              style={{ 
                                color: colors.textMuted, 
                                fontWeight: 400, 
                                fontSize: '10px',
                                marginTop: '1px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              <MapPin size={9} /> {event.location}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Drag-to-create overlay for day view */}
                    {dragCreate && dragCreate.date && formatDateString(dragCreate.date) === formatDateString(selectedDate) && (() => {
                      const startTotal = dragCreate.startHour * 60 + dragCreate.startMinutes;
                      const endTotal = dragCreate.currentHour * 60 + dragCreate.currentMinutes;
                      const top = Math.min(startTotal, endTotal);
                      const height = Math.max(15, Math.abs(endTotal - startTotal));
                      return (
                        <div
                          className="absolute left-1 right-1 rounded-lg pointer-events-none"
                          style={{
                            top: `${top}px`,
                            height: `${height}px`,
                            backgroundColor: `${colors.accent}40`,
                            border: `2px dashed ${colors.accent}`,
                            zIndex: 50
                          }}
                        />
                      );
                    })()}
                    
                    {/* Click/drag to add */}
                    <div 
                      className="absolute inset-0 cursor-crosshair"
                      style={{ zIndex: 5 }}
                      onMouseDown={(e) => {
                        const target = e.currentTarget;
                        const rect = target.getBoundingClientRect();
                        const y = e.clientY - rect.top;
                        const hour = Math.floor(y / 60);
                        const minutes = Math.floor((y % 60) / 15) * 15;
                        
                        if (isAtEventLimit) {
                          setShowUpgradeModal(true);
                          return;
                        }
                        
                        dragCreateRef.current = target;
                        const newDragCreate = {
                          date: selectedDate,
                          startHour: hour,
                          startMinutes: minutes,
                          currentHour: hour,
                          currentMinutes: minutes + 15
                        };
                        dragCreateDataRef.current = newDragCreate;
                        setDragCreate(newDragCreate);
                      }}
                      onTouchStart={(e) => {
                        const target = e.currentTarget;
                        const rect = target.getBoundingClientRect();
                        const y = e.touches[0].clientY - rect.top;
                        const hour = Math.floor(y / 60);
                        const minutes = Math.floor((y % 60) / 15) * 15;
                        
                        if (isAtEventLimit) {
                          setShowUpgradeModal(true);
                          return;
                        }
                        
                        dragCreateRef.current = target;
                        const newDragCreate = {
                          date: selectedDate,
                          startHour: hour,
                          startMinutes: minutes,
                          currentHour: hour,
                          currentMinutes: minutes + 15
                        };
                        dragCreateDataRef.current = newDragCreate;
                        setDragCreate(newDragCreate);
                      }}
                      onClick={(e) => {
                        // Only trigger if not a drag operation and didn't just finish a drag
                        if (dragCreate || justFinishedDragRef.current) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const y = e.clientY - rect.top;
                        const hour = Math.floor(y / 60);
                        const minutes = Math.floor((y % 60) / 15) * 15;
                        openAddModal(selectedDate, `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );})()}

          {/* ==================== SCHEDULE VIEW ==================== */}
          {viewMode === 'schedule' && (
            <div className="max-w-3xl mx-auto h-full overflow-y-auto">
              {upcomingEvents.length === 0 ? (
                <div className="text-center py-12 rounded-lg" style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}>
                  <CalendarIcon size={40} className="mx-auto mb-4 opacity-30" style={{ color: colors.textMuted }} />
                  <p style={{ color: colors.textMuted }}>No upcoming events</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingEvents.map(({ date, events: dayEvents, tasks: dayTasks, goals: dayGoals }) => (
                    <div key={formatDateString(date)} className="rounded-lg p-4" style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}>
                      {/* Date Header */}
                      <div className="flex items-center gap-4 pb-3 mb-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <div className="flex items-center gap-3">
                          <span 
                            className={`text-xl font-medium w-10 h-10 flex items-center justify-center rounded-full transition-all ${isToday(date) ? '' : ''}`}
                            style={{ backgroundColor: isToday(date) ? colors.accent : colors.bgTertiary, color: isToday(date) ? colors.onAccent : colors.textPrimary }}
                          >
                            {date.getDate()}
                          </span>
                          <div>
                            <div className="text-sm font-medium" style={{ color: colors.textPrimary }}>
                              {date.toLocaleDateString('en-US', { weekday: 'long' })}
                            </div>
                            <div className="text-xs" style={{ color: colors.textMuted }}>
                              {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Events List */}
                      <div className="space-y-2">
                        {dayEvents.map(event => (
                          <div 
                            key={event.id}
                            onClick={() => openPreviewEvent(event)}
                            className="flex items-start gap-3 py-2 px-3 rounded-md cursor-pointer notebook-row-hover transition-all"
                          >
                            <div className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: getEventColorWithCalendars(event) }} />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs" style={{ color: colors.textMuted }}>
                                {event.startTime ? `${formatTime(event.startTime)}${event.endTime ? ` – ${formatTime(event.endTime)}` : ''}` : 'All day'}
                              </div>
                              <div className="font-medium" style={{ color: colors.textPrimary }}>{event.name}</div>
                              {event.location && (
                                <div className="text-xs flex items-center gap-1" style={{ color: colors.textMuted }}>
                                  <MapPin size={12} /> {event.location}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {/* Tasks */}
                        {taskSettings.visible && dayTasks.map(task => (
                          <div key={task.id} 
                            onClick={() => openPreviewTask(task)}
                            className="flex items-center gap-3 py-2 px-3 rounded-md cursor-pointer notebook-row-hover transition-all">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: taskSettings.color }} />
                            <div className="flex items-center gap-2">
                              <CheckSquare size={14} style={{ color: taskSettings.color }} />
                              <span style={{ color: colors.textPrimary }}>{task.title}</span>
                            </div>
                          </div>
                        ))}

                        {/* Goals */}
                        {goalSettings.visible && dayGoals.map(goal => (
                          <div key={goal.id}
                            onClick={() => openPreviewGoal(goal)}
                            className="flex items-center gap-3 py-2 px-3 rounded-md cursor-pointer notebook-row-hover transition-all">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: goalSettings.color }} />
                            <div className="flex items-center gap-2">
                              <Target size={14} style={{ color: goalSettings.color }} />
                              <span style={{ color: colors.textPrimary }}>{goal.title}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ==================== MOBILE VIEW ==================== */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between mb-3">
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 rounded-full transition-all active:scale-90" 
              style={{ color: colors.textSecondary }}
            >
              <ChevronLeft size={24} />
            </button>
            {/* Month dropdown selector */}
            <div className="flex items-center gap-2">
              <select
                value={currentDate.getMonth()}
                onChange={(e) => {
                  const newDate = new Date(currentDate);
                  newDate.setMonth(parseInt(e.target.value));
                  setCurrentDate(newDate);
                }}
                className="text-lg font-medium bg-transparent outline-none appearance-none cursor-pointer"
                style={{ color: colors.textPrimary }}
              >
                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => (
                  <option key={idx} value={idx} style={{ backgroundColor: colors.bg, color: colors.textPrimary }}>{month}</option>
                ))}
              </select>
              <select
                value={currentDate.getFullYear()}
                onChange={(e) => {
                  const newDate = new Date(currentDate);
                  newDate.setFullYear(parseInt(e.target.value));
                  setCurrentDate(newDate);
                }}
                className="text-lg font-medium bg-transparent outline-none appearance-none cursor-pointer"
                style={{ color: colors.textPrimary }}
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                  <option key={year} value={year} style={{ backgroundColor: colors.bg, color: colors.textPrimary }}>{year}</option>
                ))}
              </select>
              <button
                onClick={() => setShowMobileMiniCal(!showMobileMiniCal)}
                className="p-1"
              >
                <ChevronRight 
                  size={16} 
                  className="transition-transform duration-200" 
                  style={{ color: colors.textMuted, transform: showMobileMiniCal ? 'rotate(-90deg)' : 'rotate(90deg)' }} 
                />
              </button>
            </div>
            <button 
              onClick={() => navigate(1)} 
              className="p-2 rounded-full transition-all active:scale-90" 
              style={{ color: colors.textSecondary }}
            >
              <ChevronRight size={24} />
            </button>
          </div>
          
          {/* Collapsible Mini Calendar */}
          <div 
            className="overflow-hidden transition-all duration-150 ease-out"
            style={{ 
              maxHeight: showMobileMiniCal ? '280px' : '0px',
              opacity: showMobileMiniCal ? 1 : 0,
              marginBottom: showMobileMiniCal ? '16px' : '0px'
            }}
          >
            <div 
              className="p-3 rounded-lg" 
              style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
            >
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i} className="text-[10px] py-1" style={{ color: colors.textMuted }}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {miniCalendarDays.map((day, i) => {
                  const isCurrentDay = isToday(day.date);
                  const isSelected = selectedDate && isSameDate(day.date, selectedDate);
                  
                  // Get events, tasks, goals for this day (for dot indicators)
                  const dayEvents = getEventsForDate(day.date);
                  const dayTasks = taskSettings.visible ? getTasksForDate(day.date) : [];
                  const dayGoals = goalSettings.visible ? getGoalsForDate(day.date) : [];
                  
                  // Get unique event colors (up to 3)
                  const eventColors = dayEvents.length > 0 
                    ? [...new Set(dayEvents.map(e => getEventColorWithCalendars(e)))].slice(0, 3)
                    : [];
                  const hasTasks = dayTasks.length > 0;
                  const hasGoals = dayGoals.length > 0;
                  const hasIndicators = eventColors.length > 0 || hasTasks || hasGoals;
                  
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedDate(day.date)}
                      className="text-[11px] w-8 h-9 rounded-lg flex flex-col items-center justify-center transition-all duration-150 active:scale-90"
                      style={{ 
                        backgroundColor: isCurrentDay ? colors.accent : (isSelected ? colors.bgTertiary : 'transparent'),
                        color: isCurrentDay ? colors.onAccent : day.isCurrentMonth ? colors.textPrimary : colors.textMuted
                      }}
                    >
                      <span>{day.date.getDate()}</span>
                      {/* Dot indicators */}
                      {hasIndicators && (
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {eventColors.map((color, idx) => (
                            <div 
                              key={idx}
                              className="w-1 h-1 rounded-full"
                              style={{ backgroundColor: isCurrentDay ? colors.onAccent : color }}
                            />
                          ))}
                          {hasTasks && (
                            <div 
                              className="w-1 h-1 rounded-full"
                              style={{ backgroundColor: isCurrentDay ? colors.onAccent : taskSettings.color }}
                            />
                          )}
                          {hasGoals && (
                            <div 
                              className="w-1 h-1 rounded-full"
                              style={{ backgroundColor: isCurrentDay ? colors.onAccent : goalSettings.color }}
                            />
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Today Button */}
          {!isToday(selectedDate) && (
            <button 
              onClick={goToToday}
              className="mb-3 px-3 py-1.5 text-xs rounded-full"
              style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}
            >
              Today
            </button>
          )}
        </div>
        
        {/* Mobile Agenda/Schedule View */}
        <div className="flex-1 overflow-auto px-4 pb-24 flex flex-col">
          {/* Selected Date Header */}
          <div 
            key={selectedDate.toISOString().split('T')[0]}
            className="sticky top-0 py-3 px-3 mb-2 animate-fade-in z-10 rounded-lg"
            style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-md flex flex-col items-center justify-center transition-all duration-200"
                style={{ backgroundColor: isToday(selectedDate) ? colors.accent : colors.bgSecondary }}
              >
                <span className="text-xs font-medium" style={{ color: isToday(selectedDate) ? colors.onAccent : colors.textMuted }}>
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
                <span className="text-lg font-semibold" style={{ color: isToday(selectedDate) ? colors.onAccent : colors.textPrimary }}>
                  {selectedDate.getDate()}
                </span>
              </div>
              <div>
                <div className="font-medium" style={{ color: colors.textPrimary }}>
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}
                </div>
                <div className="text-sm" style={{ color: colors.textMuted }}>
                  {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>
              </div>
            </div>
          </div>
          
          {/* Events for Selected Date - Min height to prevent layout jump */}
          <div className="min-h-[200px]">
          {(() => {
            const dayEvents = getEventsForDate(selectedDate);
            const dayTasks = taskSettings.visible ? getTasksForDate(selectedDate) : [];
            const dayGoals = goalSettings.visible ? getGoalsForDate(selectedDate) : [];
            const allItems = [
              ...dayEvents.map(e => ({ ...e, itemType: 'event' })),
              ...dayTasks.map(t => ({ ...t, itemType: 'task', name: t.title })),
              ...dayGoals.map(g => ({ ...g, itemType: 'goal', name: g.title }))
            ].sort((a, b) => {
              // Sort by time, all-day events first
              if (!a.startTime && b.startTime) return -1;
              if (a.startTime && !b.startTime) return 1;
              return (a.startTime || '').localeCompare(b.startTime || '');
            });
            
            if (allItems.length === 0) {
              return (
                <div className="text-center py-12 animate-fade-in">
                  <CalendarIcon size={40} className="mx-auto mb-3 opacity-30" style={{ color: colors.textMuted }} />
                  <p style={{ color: colors.textMuted }}>No events for this day</p>
                </div>
              );
            }
            
            return (
              <div className="space-y-3">
                {allItems.map((item, idx) => (
                  <button
                    key={item.id || idx}
                    onClick={() => {
                      if (item.itemType === 'event') openPreviewEvent(item);
                      else if (item.itemType === 'task') openPreviewTask(item);
                      else if (item.itemType === 'goal') openPreviewGoal(item);
                    }}
                    className="w-full text-left p-4 rounded-lg transition-all duration-150 active:scale-[0.98] hover:translate-x-1"
                    style={{ 
                      backgroundColor: colors.bgSecondary,
                      animationDelay: `${idx * 50}ms`
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {item.itemType === 'task' && <CheckSquare size={16} style={{ color: taskSettings.color }} />}
                        {item.itemType === 'goal' && <Target size={16} style={{ color: goalSettings.color }} />}
                        {item.itemType === 'event' && <CalendarIcon size={16} style={{ color: getEventColorWithCalendars(item) }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate" style={{ color: colors.textPrimary }}>
                          {item.name}
                        </div>
                        {item.startTime && (
                          <div className="text-sm mt-0.5" style={{ color: colors.textMuted }}>
                            {formatTime(item.startTime)}{item.endTime && ` – ${formatTime(item.endTime)}`}
                          </div>
                        )}
                        {!item.startTime && item.itemType === 'event' && (
                          <div className="text-xs mt-0.5" style={{ color: colors.textMuted }}>All day</div>
                        )}
                        {item.location && (
                          <div className="text-sm mt-1 flex items-center gap-1" style={{ color: colors.textMuted }}>
                            <MapPin size={12} />
                            <span className="truncate">{item.location}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            );
          })()}
          </div>
          
          {/* Upcoming Days Preview - Pushed to bottom with margin-top auto */}
          <div className="mt-auto pt-6">
            <h3 className="text-xs mb-3 font-medium" style={{ color: colors.textMuted }}>Upcoming</h3>
            {upcomingEvents.slice(0, 7).filter(d => !isSameDate(d.date, selectedDate)).slice(0, 5).map((dateInfo, idx) => {
              const itemCount = dateInfo.events.length + (taskSettings.visible ? dateInfo.tasks.length : 0) + (goalSettings.visible ? dateInfo.goals.length : 0);
              
              if (itemCount === 0) return null;
              
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDate(dateInfo.date)}
                  className="w-full text-left py-3 border-b flex items-center gap-3 transition-all duration-150 active:scale-[0.98] hover:pl-2"
                  style={{ borderColor: colors.border }}
                >
                  <div className="w-10 text-center">
                    <div className="text-xs font-medium" style={{ color: colors.textMuted }}>
                      {dateInfo.date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="font-medium" style={{ color: colors.textPrimary }}>
                      {dateInfo.date.getDate()}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm" style={{ color: colors.textPrimary }}>
                      {itemCount} item{itemCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <ChevronRight size={16} className="transition-transform" style={{ color: colors.textMuted }} />
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Calendar Management - Mobile */}
        <div className="px-4 py-4 border-t" style={{ borderColor: colors.border }}>
          <div className="space-y-2">
            {/* Always show Manage Calendars button */}
            <button
              onClick={() => setShowMobileCalendarManager(true)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-md text-sm transition-all active:scale-95"
              style={{ backgroundColor: colors.bgSecondary, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
            >
              <CalendarIcon size={16} style={{ color: colors.accent }} />
              Manage Calendars
            </button>
            
            {/* Google Calendar status */}
            {!googleCalendarConnected ? (
              <button 
                onClick={() => {
                  if (canConnectGoogle) connectGoogleCalendar();
                }}
                disabled={isConnectingCalendar || !canConnectGoogle}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-md text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: colors.bgSecondary, color: canConnectGoogle ? colors.accent : colors.textMuted, border: `1px solid ${colors.border}` }}
                title={!canConnectGoogle ? 'Upgrade to Pro to connect Google Calendar' : ''}
              >
                {isConnectingCalendar ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Connect Google Calendar
                    {!canConnectGoogle && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: colors.accent, color: colors.onAccent }}>Pro</span>}
                  </>
                )}
              </button>
            ) : (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-xs mb-1" style={{ color: colors.success }}>
                  <Check size={12} /> Google Calendar Connected
                </div>
                <button 
                  onClick={disconnectGoogleCalendar}
                  className="text-xs transition-opacity hover:opacity-70"
                  style={{ color: colors.textMuted }}
                >
                  Disconnect
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile FAB - Portal to escape transformed PageWrapper - Hidden when modals are open */}
      {!showEventModal && !showAddCalendar && !showMobileCalendarManager && !showPreviewModal && createPortal(
        <button
          onClick={() => openAddModal(selectedDate || new Date())}
          className={`fixed md:hidden w-12 h-12 rounded-full  flex items-center justify-center z-[9999] transition-all active:scale-90 ${isAtEventLimit ? 'opacity-75' : ''}`}
          style={{ 
            backgroundColor: colors.accent, 
            boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.12)',
            right: '16px',
            // Position above the bottom tab bar on iOS (tab bar ~60px + safe area)
            bottom: 'calc(var(--safe-area-inset-bottom, 0px) + 80px)',
          }}
          title={isAtEventLimit ? `Limit reached - tap to upgrade` : 'Add Event'}
        >
          <Plus size={22} color={colors.onAccent} />
        </button>,
        document.body
      )}

      {/* ==================== CALENDAR MODAL ==================== */}
      {showAddCalendar && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 animate-fade-in">
          <div className="w-full max-w-sm rounded-lg p-6 animate-scale-up" style={{ 
            ...getModalStyle(colors)
          }}>
            <h3 className="text-lg mb-4 font-semibold tracking-tight" style={{ color: colors.textPrimary }}>
              {editingCalendar ? 'Edit Calendar' : 'Add Calendar'}
            </h3>
            
            <input
              type="text"
              value={calendarForm.name}
              onChange={(e) => setCalendarForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Calendar name"
              className="w-full px-4 py-2 rounded-md mb-4 outline-none"
              style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
            />
            
            <div className="mb-4">
              <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Color</label>
              <div className="flex flex-wrap gap-2">
                {CALENDAR_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setCalendarForm(f => ({ ...f, color }))}
                    className={`w-8 h-8 rounded-full transition-all ${calendarForm.color === color ? 'ring-2 ring-offset-2 scale-110' : ''}`}
                    style={{ backgroundColor: color, ringColor: color, ringOffsetColor: colors.bgSecondary }}
                  />
                ))}
              </div>
            </div>
            
            {/* Google Calendar Sync Toggle */}
            {googleCalendarConnected && (
              <div className="mb-4 p-3 rounded-md" style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}>
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <RefreshCw size={16} style={{ color: calendarForm.syncToGoogle ? colors.accent : colors.textMuted }} />
                    <div>
                      <div className="text-sm" style={{ color: colors.textPrimary }}>Sync to Google Calendar</div>
                      <div className="text-xs" style={{ color: colors.textMuted }}>
                        {calendarForm.syncToGoogle 
                          ? `Events sync as "${calendarForm.name || 'Calendar'} - mind."`
                          : 'Events stay local only'
                        }
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCalendarForm(f => ({ ...f, syncToGoogle: !f.syncToGoogle }))}
                    className="relative w-11 h-6 rounded-full transition-colors"
                    style={{ backgroundColor: calendarForm.syncToGoogle ? colors.accent : colors.bgTertiary }}
                  >
                    <span 
                      className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                      style={{ transform: calendarForm.syncToGoogle ? 'translateX(20px)' : 'translateX(0)' }}
                    />
                  </button>
                </label>
                {editingCalendar?.googleCalendarId && (
                  <div className="mt-2 text-xs flex items-center gap-1" style={{ color: colors.textMuted }}>
                    <Check size={12} style={{ color: colors.accent }} />
                    Linked to Google Calendar
                  </div>
                )}
              </div>
            )}
            
            <div className="flex gap-3">
              {editingCalendar && editingCalendar.id !== 'default' && (
                <button
                  onClick={() => { deleteCalendar(editingCalendar.id); setShowAddCalendar(false); setEditingCalendar(null); }}
                  className="px-3 py-2 rounded-md"
                  style={{ color: colors.error, border: `1px solid ${colors.error}` }}
                  disabled={syncingCalendar}
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button
                onClick={() => { setShowAddCalendar(false); setEditingCalendar(null); }}
                className="flex-1 py-2 rounded-md"
                style={{ border: `1px solid ${colors.border}`, color: colors.textSecondary }}
                disabled={syncingCalendar}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCalendar}
                className="flex-1 py-2 rounded-md flex items-center justify-center gap-2"
                style={{ backgroundColor: colors.accent, color: colors.onAccent, opacity: syncingCalendar ? 0.7 : 1 }}
                disabled={syncingCalendar}
              >
                {syncingCalendar ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    Syncing...
                  </>
                ) : (
                  editingCalendar ? 'Save' : 'Add'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ==================== TASKS/GOALS COLOR MODAL ==================== */}
      {editingSpecialCalendar && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 animate-fade-in">
          <div className="w-full max-w-sm rounded-lg p-6 animate-scale-up" style={{ 
            ...getModalStyle(colors)
          }}>
            <h3 className="text-lg mb-4 flex items-center gap-2 font-semibold tracking-tight" style={{ color: colors.textPrimary }}>
              {editingSpecialCalendar === 'tasks' ? <CheckSquare size={20} style={{ color: taskSettings.color }} /> : <Target size={20} style={{ color: goalSettings.color }} />}
              {editingSpecialCalendar === 'tasks' ? 'Tasks Settings' : 'Goals Settings'}
            </h3>
            
            <div className="mb-4">
              <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Color</label>
              <div className="flex flex-wrap gap-2">
                {CALENDAR_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => {
                      if (editingSpecialCalendar === 'tasks') {
                        setTaskSettings(prev => ({ ...prev, color }));
                      } else {
                        setGoalSettings(prev => ({ ...prev, color }));
                      }
                    }}
                    className={`w-8 h-8 rounded-full transition-all ${
                      (editingSpecialCalendar === 'tasks' ? taskSettings.color : goalSettings.color) === color 
                        ? 'ring-2 ring-offset-2 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color, ringColor: color, ringOffsetColor: colors.bgSecondary }}
                  />
                ))}
              </div>
            </div>
            
            {/* Google Calendar Sync Toggle */}
            {googleCalendarConnected && (
              <div className="mb-4 p-3 rounded-md" style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}>
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <RefreshCw size={16} style={{ color: (editingSpecialCalendar === 'tasks' ? taskSettings.syncToGoogle : goalSettings.syncToGoogle) ? colors.accent : colors.textMuted }} />
                    <div>
                      <div className="text-sm" style={{ color: colors.textPrimary }}>Sync to Google Calendar</div>
                      <div className="text-xs" style={{ color: colors.textMuted }}>
                        {(editingSpecialCalendar === 'tasks' ? taskSettings.syncToGoogle : goalSettings.syncToGoogle)
                          ? `Syncs as "${editingSpecialCalendar === 'tasks' ? 'Tasks' : 'Goals'} - mind."`
                          : `${editingSpecialCalendar === 'tasks' ? 'Tasks' : 'Goals'} stay local only`
                        }
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const settings = editingSpecialCalendar === 'tasks' ? taskSettings : goalSettings;
                      const setSettings = editingSpecialCalendar === 'tasks' ? setTaskSettings : setGoalSettings;
                      const newSyncValue = !settings.syncToGoogle;
                      
                      // If enabling sync, check if we need to create a new calendar
//                      if (newSyncValue && googleAccessToken) {
//                        setSyncingSpecialCalendar(true);
//                        try {
//                          const calendarService = new GoogleCalendarService(googleAccessToken);
//                          let googleCalId = settings.googleCalendarId;
//                          
//                          // Check if existing calendar still exists in Google
//                          if (googleCalId) {
//                            const exists = await calendarService.calendarExists(googleCalId);
//                            if (!exists) {
//                              googleCalId = null;
//                            }
//                          }
//                          
//                          // Create calendar if needed
//                          if (!googleCalId) {
//                            const calendarName = editingSpecialCalendar === 'tasks' ? 'Tasks - mind.' : 'Goals - mind.';
//                            const googleCal = await calendarService.createCalendar(calendarName, settings.color);
//                            googleCalId = googleCal.id;
//                          }
//                          
//                          setSettings(prev => ({ ...prev, syncToGoogle: true, googleCalendarId: googleCalId }));
//                          
//                          // Sync existing items to the calendar
//                          if (editingSpecialCalendar === 'tasks') {
//                            for (const task of tasks.filter(t => !t.completed && t.dueDate)) {
//                              try {
//                                const taskEvent = {
//                                  name: task.title,
//                                  startDate: task.dueDate,
//                                  date: task.dueDate,
//                                  description: task.description || '',
//                                };
//                                const result = await calendarService.createEvent(taskEvent, googleCalId);
//                                await updateTask(task.id, { googleEventId: result.id });
//                              } catch (err) {
//                              }
//                            }
//                          } else {
//                            for (const goal of goals.filter(g => !g.completed && g.targetDate)) {
//                              try {
//                                const milestonesText = goal.milestones?.length > 0 
//                                  ? '\n\nMilestones:\n' + goal.milestones.map(m => `${m.completed ? '✓' : '○'} ${m.title}`).join('\n')
//                                  : '';
//                                const goalEvent = {
//                                  name: goal.title,
//                                  startDate: goal.targetDate,
//                                  date: goal.targetDate,
//                                  description: (goal.description || '') + milestonesText,
//                                };
//                                const result = await calendarService.createEvent(goalEvent, googleCalId);
//                                await updateGoal(goal.id, { googleEventId: result.id });
//                              } catch (err) {
//                              }
//                            }
//                          }
//                        } catch (error) {
//                          alert('Failed to create Google Calendar: ' + error.message);
//                        } finally {
//                          setSyncingSpecialCalendar(false);
//                        }
//                      } else {
//                        setSettings(prev => ({ ...prev, syncToGoogle: newSyncValue }));
//                      }
                    }}
//                    className="relative w-11 h-6 rounded-full transition-colors"
                    style={{ backgroundColor: (editingSpecialCalendar === 'tasks' ? taskSettings.syncToGoogle : goalSettings.syncToGoogle) ? colors.accent : colors.bgTertiary }}
                    disabled={syncingSpecialCalendar}
                  >
                    <span 
                      className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                      style={{ transform: (editingSpecialCalendar === 'tasks' ? taskSettings.syncToGoogle : goalSettings.syncToGoogle) ? 'translateX(20px)' : 'translateX(0)' }}
                    />
                  </button>
                </label>
                {(editingSpecialCalendar === 'tasks' ? taskSettings.googleCalendarId : goalSettings.googleCalendarId) && (
                  <div className="mt-2 text-xs flex items-center gap-1" style={{ color: colors.textMuted }}>
                    <Check size={12} style={{ color: colors.accent }} />
                    Linked to Google Calendar
                  </div>
                )}
                {syncingSpecialCalendar && (
                  <div className="mt-2 text-xs flex items-center gap-1" style={{ color: colors.accent }}>
                    <RefreshCw size={12} className="animate-spin" />
                    Creating calendar and syncing...
                  </div>
                )}
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => setEditingSpecialCalendar(null)}
                className="flex-1 py-2 rounded-md"
                style={{ border: `1px solid ${colors.border}`, color: colors.textSecondary }}
                disabled={syncingSpecialCalendar}
              >
                Cancel
              </button>
              <button
                onClick={() => setEditingSpecialCalendar(null)}
                className="flex-1 py-2 rounded-md"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                disabled={syncingSpecialCalendar}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ==================== GOOGLE CALENDAR COLOR MODAL ==================== */}
      {editingGoogleCalendar && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 animate-fade-in">
          <div className="w-full max-w-sm rounded-lg p-6 animate-scale-up" style={{ 
            ...getModalStyle(colors)
          }}>
            <h3 className="text-lg mb-4 flex items-center gap-2 font-semibold tracking-tight" style={{ color: colors.textPrimary }}>
              <CalendarIcon size={20} style={{ color: editingGoogleCalendar.displayColor }} />
              <span className="truncate">{editingGoogleCalendar.name}</span>
            </h3>
            
            <div className="mb-4">
              <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Color</label>
              <div className="flex flex-wrap gap-2">
                {CALENDAR_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => {
                      devLog('[GoogleCalColor] Setting color:', { calendarId: editingGoogleCalendar.id, color });
                      setGoogleCalendarCustomColor(editingGoogleCalendar.id, color);
                      setEditingGoogleCalendar({ ...editingGoogleCalendar, displayColor: color });
                      // Update googleCalendars state to reflect the change immediately
                      // Force a new array to trigger re-render
                      setGoogleCalendars(prev => {
                        const updated = prev.map(cal => 
                          cal.id === editingGoogleCalendar.id ? { ...cal, color: color } : cal
                        );
                        devLog('[GoogleCalColor] Updated googleCalendars:', updated.find(c => c.id === editingGoogleCalendar.id));
                        // Re-save to sync with Dashboard
                        saveGoogleCalendarColors(updated);
                        return [...updated]; // New array reference
                      });
                    }}
                    className={`w-8 h-8 rounded-full transition-all ${
                      editingGoogleCalendar.displayColor === color 
                        ? 'ring-2 ring-offset-2 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color, ringColor: color, ringOffsetColor: colors.bgSecondary }}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setEditingGoogleCalendar(null)}
                className="flex-1 py-2 rounded-md"
                style={{ border: `1px solid ${colors.border}`, color: colors.textSecondary }}
              >
                Cancel
              </button>
              <button
                onClick={() => setEditingGoogleCalendar(null)}
                className="flex-1 py-2 rounded-md"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ==================== PREVIEW MODAL (Read-only) ==================== */}
      {showPreviewModal && previewItem && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center sm:p-4 bg-black/60 animate-fade-in" onClick={() => setShowPreviewModal(false)}>
          <div 
            className="sheet-safe w-full h-full sm:h-auto sm:max-w-md p-5 sm:p-6 sm:max-h-[80vh] overflow-y-auto relative animate-scale-up" 
            style={{ 
              ...getModalStyle(colors)
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Action buttons row - top right */}
            <div className="absolute top-4 right-4 flex items-center gap-1">
              {/* Download iCal button for events */}
              {previewItem.type === 'event' && (
                <button 
                  onClick={() => {
                    downloadICalEvent(previewItem.data);
                  }} 
                  className="p-2 rounded-full notebook-row-hover transition-all"
                  style={{ color: colors.textMuted }}
                  title="Add to Calendar (download .ics)"
                >
                  <Download size={20} />
                </button>
              )}
              {/* Delete button for events */}
              {previewItem.type === 'event' && (
                <button 
                  onClick={() => {
                    setShowPreviewModal(false);
                    handleDeleteEvent(previewItem.data);
                  }} 
                  className="p-2 rounded-full notebook-row-hover transition-all"
                  style={{ color: colors.textMuted }}
                  title="Delete event"
                >
                  <Trash2 size={20} />
                </button>
              )}
              {/* Close button */}
              <button 
                onClick={() => setShowPreviewModal(false)} 
                className="p-2 rounded-full notebook-row-hover transition-all"
                style={{ color: colors.textMuted }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Event Preview */}
            {previewItem.type === 'event' && (() => {
              const event = previewItem.data;
              const calendar = googleCalendars.find(c => c.id === event.calendarId) || localCalendars.find(c => c.id === event.calendarId);
              return (
                <div>
                  {/* Warning: Deleted from Google */}
                  {event.deletedFromGoogle && (
                    <div className="mb-4 p-3 rounded-md flex items-center gap-2" style={{ border: `1px solid ${colors.textPrimary}` }}>
                      <AlertTriangle size={14} className="flex-shrink-0" style={{ color: colors.warning }} />
                      <span className="text-sm" style={{ color: colors.textPrimary }}>This event was deleted from Google Calendar</span>
                    </div>
                  )}

                  {/* Color indicator and title */}
                  <div className="flex items-start gap-3 mb-4 pr-8">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: getEventColorWithCalendars(event) }} />
                    <h2 className="text-xl font-semibold" style={{ color: colors.textPrimary }}>{event.name}</h2>
                  </div>

                  {/* Date & Time */}
                  <div className="flex items-center gap-3 mb-3" style={{ color: colors.textSecondary }}>
                    <CalendarIcon size={18} />
                    <div>
                      <div className="text-sm">
                        {new Date(event.startDate || event.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      </div>
                      {event.startTime && (
                        <div className="text-sm" style={{ color: colors.textMuted }}>
                          {formatTime(event.startTime)}{event.endTime ? ` – ${formatTime(event.endTime)}` : ''}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location */}
                  {event.location && (
                    <div className="flex items-center gap-3 mb-3" style={{ color: colors.textSecondary }}>
                      <MapPin size={18} />
                      <span className="text-sm">{event.location}</span>
                    </div>
                  )}

                  {/* Calendar name */}
                  {calendar && (
                    <div className="flex items-center gap-3 mb-3" style={{ color: colors.textSecondary }}>
                      <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: calendar.color }} />
                      <span className="text-sm">{calendar.name}</span>
                    </div>
                  )}

                  {/* Description */}
                  {event.description && (
                    <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                      <div className="text-sm whitespace-pre-wrap" style={{ color: colors.textSecondary }}>
                        {renderDescription(event.description, colors, getEventColorWithCalendars(event))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Task Preview */}
            {previewItem.type === 'task' && (() => {
              const task = previewItem.data;
              const category = taskCategories.find(c => c.value === task.category);
              const weight = weightOptions.find(w => w.value === task.weight);
              return (
                <div>
                  {/* Color indicator and title */}
                  <div className="flex items-start gap-3 mb-4 pr-8">
                    <CheckSquare size={20} className="flex-shrink-0 mt-0.5" style={{ color: taskSettings.color }} />
                    <h2 className="text-xl font-semibold" style={{ color: colors.textPrimary }}>{task.title}</h2>
                  </div>

                  {/* Due Date */}
                  {task.dueDate && (
                    <div className="flex items-center gap-3 mb-3" style={{ color: colors.textSecondary }}>
                      <CalendarIcon size={18} />
                      <span className="text-sm">
                        Due: {new Date(task.dueDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  )}

                  {/* Category */}
                  {category && (
                    <div className="flex items-center gap-3 mb-3" style={{ color: colors.textSecondary }}>
                      <List size={18} />
                      <span className="text-sm">{category.label}</span>
                    </div>
                  )}

                  {/* Priority */}
                  {weight && (
                    <div className="flex items-center gap-3 mb-3" style={{ color: colors.textSecondary }}>
                      <Flag size={18} />
                      <span className="text-sm px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: weight.color + '30', color: weight.color }}>
                        {weight.label}
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  {task.description && (
                    <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                      <div className="text-sm whitespace-pre-wrap" style={{ color: colors.textSecondary }}>{task.description}</div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Goal Preview */}
            {previewItem.type === 'goal' && (() => {
              const goal = previewItem.data;
              const timeframe = GOAL_TIMEFRAMES.find(t => t.value === goal.timeframe);
              return (
                <div>
                  {/* Color indicator and title */}
                  <div className="flex items-start gap-3 mb-4 pr-8">
                    <Target size={20} className="flex-shrink-0 mt-0.5" style={{ color: goalSettings.color }} />
                    <h2 className="text-xl font-semibold" style={{ color: colors.textPrimary }}>{goal.title}</h2>
                  </div>

                  {/* Timeframe */}
                  {timeframe && (
                    <div className="flex items-center gap-3 mb-3" style={{ color: colors.textSecondary }}>
                      <timeframe.icon size={18} />
                      <span className="text-sm">{timeframe.label} Goal</span>
                    </div>
                  )}

                  {/* Target Date */}
                  {goal.targetDate && (
                    <div className="flex items-center gap-3 mb-3" style={{ color: colors.textSecondary }}>
                      <CalendarIcon size={18} />
                      <span className="text-sm">
                        Target: {new Date(goal.targetDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  )}

                  {/* SMART Details */}
                  {(goal.specific || goal.measurable || goal.achievable || goal.relevant || goal.timeBound) && (
                    <div className="mt-4 pt-4 space-y-3" style={{ borderTop: `1px solid ${colors.border}` }}>
                      {goal.specific && (
                        <div>
                          <div className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Specific</div>
                          <div className="text-sm" style={{ color: colors.textSecondary }}>{goal.specific}</div>
                        </div>
                      )}
                      {goal.measurable && (
                        <div>
                          <div className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Measurable</div>
                          <div className="text-sm" style={{ color: colors.textSecondary }}>{goal.measurable}</div>
                        </div>
                      )}
                      {goal.achievable && (
                        <div>
                          <div className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Achievable</div>
                          <div className="text-sm" style={{ color: colors.textSecondary }}>{goal.achievable}</div>
                        </div>
                      )}
                      {goal.relevant && (
                        <div>
                          <div className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Relevant</div>
                          <div className="text-sm" style={{ color: colors.textSecondary }}>{goal.relevant}</div>
                        </div>
                      )}
                      {goal.timeBound && (
                        <div>
                          <div className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Time-Bound</div>
                          <div className="text-sm" style={{ color: colors.textSecondary }}>{goal.timeBound}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Edit button - only show if editable */}
            {isItemEditable(previewItem.data, previewItem.type) && (
              <button
                onClick={switchToEdit}
                className="absolute bottom-6 right-6 p-3 rounded-full transition-all"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                <Edit2 size={20} />
              </button>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ==================== EVENT MODAL ==================== */}
      {showEventModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
          <div className="sheet-safe w-full h-full sm:h-auto sm:max-w-lg sm:rounded-lg p-5 sm:p-6 sm:max-h-[90vh] overflow-y-auto animate-scale-up" style={{ 
            ...getModalStyle(colors),
            paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))',
            paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))'
          }}>
            <div className="flex items-center justify-between mb-5 sm:mb-6">
              <h3 className="text-xl sm:text-lg font-semibold tracking-tight" style={{ color: colors.textPrimary }}>
                {editingEvent ? 'Edit Event' : 'Add Event'}
              </h3>
              <button onClick={() => setShowEventModal(false)} className="p-2 -mr-2 rounded-full" style={{ color: colors.textMuted }}><X size={24} /></button>
            </div>

            <div className="space-y-5">
              <input
                type="text"
                value={eventForm.name}
                onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                placeholder="Event name"
                className="w-full px-4 py-4 sm:py-3 rounded-md text-lg outline-none"
                style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />

              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Calendar</label>
                <select
                  value={eventForm.calendarId}
                  onChange={(e) => setEventForm({ ...eventForm, calendarId: e.target.value })}
                  className="w-full px-4 py-3 rounded-md outline-none text-base"
                  style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
                >
                  <optgroup label="My Calendars">
                    {localCalendars.map(cal => (
                      <option key={cal.id} value={cal.id}>{cal.name}</option>
                    ))}
                  </optgroup>
                  {googleCalendars.filter(c => c.canWrite && !c.name.endsWith(' - mind.')).length > 0 && (
                    <optgroup label="Google Calendars">
                      {googleCalendars.filter(c => c.canWrite && !c.name.endsWith(' - mind.')).map(cal => (
                        <option key={cal.id} value={cal.id}>{cal.name}</option>
                      ))}
                    </optgroup>
                  )}
                  {googleCalendars.filter(c => !c.canWrite && !c.name.endsWith(' - mind.')).length > 0 && (
                    <optgroup label="Read-Only Calendars">
                      {googleCalendars.filter(c => !c.canWrite && !c.name.endsWith(' - mind.')).map(cal => (
                        <option key={cal.id} value={cal.id} disabled style={{ color: '#888' }}>{cal.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              <div className="grid grid-cols-1 xs:grid-cols-2 gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <div style={{ minWidth: 0 }}>
                  <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Start Date *</label>
                  <input type="date" value={eventForm.startDate}
                    onChange={(e) => setEventForm({ ...eventForm, startDate: e.target.value })}
                    className="w-full py-3 rounded-md outline-none"
                    style={{ 
                      backgroundColor: colors.bg, 
                      color: colors.textPrimary, 
                      border: `1px solid ${colors.border}`,
                      minWidth: 0,
                      fontSize: '14px',
                      paddingLeft: '8px',
                      paddingRight: '4px',
                      WebkitAppearance: 'none'
                    }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>End Date</label>
                  <input type="date" value={eventForm.endDate}
                    onChange={(e) => setEventForm({ ...eventForm, endDate: e.target.value })}
                    className="w-full py-3 rounded-md outline-none"
                    style={{ 
                      backgroundColor: colors.bg, 
                      color: colors.textPrimary, 
                      border: `1px solid ${colors.border}`,
                      minWidth: 0,
                      fontSize: '14px',
                      paddingLeft: '8px',
                      paddingRight: '4px',
                      WebkitAppearance: 'none'
                    }} />
                </div>
              </div>

              {/* All Day Toggle */}
              <div 
                className="flex items-center justify-between px-4 py-4 rounded-md cursor-pointer active:opacity-80"
                style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                onClick={() => setEventForm({ ...eventForm, isAllDay: !eventForm.isAllDay, startTime: '', endTime: '' })}
              >
                <span className="text-base" style={{ color: colors.textPrimary }}>All day</span>
                <div 
                  className="w-12 h-7 rounded-full relative transition-colors duration-200"
                  style={{ backgroundColor: eventForm.isAllDay ? colors.accent : colors.border }}
                >
                  <div 
                    className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all duration-200 "
                    style={{ left: eventForm.isAllDay ? '24px' : '4px' }}
                  />
                </div>
              </div>

              {/* Time fields - animate height based on all-day toggle */}
              <div 
                className="overflow-hidden transition-all duration-200 ease-out"
                style={{ 
                  maxHeight: eventForm.isAllDay ? '0px' : '120px',
                  opacity: eventForm.isAllDay ? 0 : 1,
                }}
              >
                <div className="grid gap-2 pt-3" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                  <div style={{ minWidth: 0 }}>
                    <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Start Time</label>
                    <input type="time" value={eventForm.startTime} step="900"
                      onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })}
                      className="w-full py-3 rounded-md outline-none"
                      style={{ 
                        backgroundColor: colors.bg, 
                        color: colors.textPrimary, 
                        border: `1px solid ${colors.border}`,
                        minWidth: 0,
                        fontSize: '14px',
                        paddingLeft: '8px',
                        paddingRight: '4px',
                        WebkitAppearance: 'none'
                      }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>End Time</label>
                    <input type="time" value={eventForm.endTime} step="900"
                      onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })}
                      className="w-full py-3 rounded-md outline-none"
                      style={{ 
                        backgroundColor: colors.bg, 
                        color: colors.textPrimary, 
                        border: `1px solid ${colors.border}`,
                        minWidth: 0,
                        fontSize: '14px',
                        paddingLeft: '8px',
                        paddingRight: '4px',
                        WebkitAppearance: 'none'
                      }} />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Location</label>
                <div className="flex items-center gap-2 px-4 py-3 rounded-md" style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}>
                  <MapPin size={18} style={{ color: colors.textMuted }} />
                  <input type="text" value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    placeholder="Add location" className="flex-1 bg-transparent outline-none text-base" style={{ color: colors.textPrimary }} />
                </div>
              </div>

              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Description</label>
                <textarea value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Add description..." rows={3}
                  className="w-full px-4 py-3 rounded-md outline-none resize-none text-base"
                  style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }} />
              </div>

              <div className="flex gap-3 pt-4">
                {editingEvent && (() => {
                  // Check if this is a read-only Google calendar event or non-editable event type (like birthdays)
                  const googleCal = googleCalendars.find(c => c.id === editingEvent.calendarId);
                  const isReadOnly = (googleCal && !googleCal.canWrite) || (editingEvent.isGoogleEvent && editingEvent.canEdit === false);
                  if (isReadOnly) return null;
                  return (
                    <button onClick={() => { handleDeleteEvent(editingEvent); setShowEventModal(false); }}
                      className="px-4 py-2 rounded-md" style={{ color: colors.error, border: `1px solid ${colors.error}` }}>
                      <Trash2 size={16} />
                    </button>
                  );
                })()}
                <button onClick={() => setShowEventModal(false)} className="flex-1 py-2 rounded-md"
                  style={{ border: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                  Cancel
                </button>
                {(() => {
                  // Check if this is a read-only Google calendar event or non-editable event type (like birthdays)
                  const googleCal = editingEvent ? googleCalendars.find(c => c.id === editingEvent.calendarId) : null;
                  const isReadOnly = (googleCal && !googleCal.canWrite) || (editingEvent?.isGoogleEvent && editingEvent?.canEdit === false);
                  if (isReadOnly) {
                    const reason = editingEvent?.eventType === 'birthday' ? 'Birthday events cannot be edited' : 'Read-only calendar';
                    return (
                      <div className="flex-1 py-2 rounded-md text-center text-sm" style={{ color: colors.textMuted }}>
                        {reason}
                      </div>
                    );
                  }
                  return (
                    <button onClick={handleSubmit} disabled={savingEvent} className="flex-1 py-2 rounded-md font-medium flex items-center justify-center gap-2"
                      style={{ backgroundColor: savingEvent ? colors.border : colors.accent, color: savingEvent ? colors.textMuted : colors.onAccent }}>
                      {savingEvent && <RefreshCw size={16} className="animate-spin" />}
                      {savingEvent ? 'Saving...' : (editingEvent ? 'Save Changes' : 'Add Event')}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Task Modal */}
      {showTaskModal && editingTask && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
          <div className="sheet-safe w-full h-full sm:h-auto sm:max-w-md p-5 sm:p-6 sm:max-h-[85vh] overflow-y-auto animate-scale-up" style={{ 
            ...getModalStyle(colors)
          }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: colors.textPrimary }}>Edit Task</h2>
              <button onClick={() => setShowTaskModal(false)} className="p-2 rounded-full hover:opacity-80" style={{ color: colors.textMuted }}>
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Title</label>
                <input type="text" value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-md outline-none"
                  style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
                  placeholder="Task title" />
              </div>

              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Description</label>
                <textarea value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  rows={3} className="w-full px-4 py-2 rounded-md outline-none resize-none"
                  style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
                  placeholder="Add description..." />
              </div>

              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Due Date</label>
                <input type="date" value={taskForm.dueDate}
                  onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                  className="w-full px-4 py-2 rounded-md outline-none"
                  style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }} />
              </div>

              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Category</label>
                <select value={taskForm.category}
                  onChange={(e) => setTaskForm({ ...taskForm, category: e.target.value })}
                  className="w-full px-4 py-2 rounded-md outline-none"
                  style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}>
                  {taskCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Priority</label>
                <div className="flex gap-2">
                  {weightOptions.map(opt => (
                    <button key={opt.value} onClick={() => setTaskForm({ ...taskForm, weight: opt.value })}
                      className="flex-1 py-2 px-3 rounded-md flex items-center justify-center gap-1 transition-all"
                      style={{
                        backgroundColor: taskForm.weight === opt.value ? opt.color : colors.bg,
                        color: taskForm.weight === opt.value ? colors.onAccent : colors.textSecondary,
                        border: `1px solid ${taskForm.weight === opt.value ? opt.color : colors.border}`
                      }}>
                      <Flag size={14} />
                      <span className="text-xs">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button onClick={handleTaskDelete}
                  className="px-4 py-2 rounded-md" style={{ color: colors.error, border: `1px solid ${colors.error}` }}>
                  <Trash2 size={16} />
                </button>
                <button onClick={() => setShowTaskModal(false)} className="flex-1 py-2 rounded-md"
                  style={{ border: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                  Cancel
                </button>
                <button onClick={handleTaskSave} className="flex-1 py-2 rounded-md font-medium"
                  style={{ backgroundColor: colors.accent, color: colors.onAccent }}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Goal Modal */}
      {showGoalModal && editingGoal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
          <div className="sheet-safe w-full h-full sm:h-auto sm:max-w-md p-5 sm:p-6 sm:max-h-[85vh] overflow-y-auto animate-scale-up" style={{ 
            ...getModalStyle(colors)
          }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: colors.textPrimary }}>Edit Goal</h2>
              <button onClick={() => setShowGoalModal(false)} className="p-2 rounded-full hover:opacity-80" style={{ color: colors.textMuted }}>
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Goal Title</label>
                <input type="text" value={goalForm.title}
                  onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-md outline-none"
                  style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
                  placeholder="What do you want to achieve?" />
              </div>

              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Timeframe</label>
                <div className="flex gap-2">
                  {GOAL_TIMEFRAMES.map(tf => (
                    <button key={tf.value} onClick={() => setGoalForm({ ...goalForm, timeframe: tf.value })}
                      className="flex-1 py-2 px-3 rounded-md text-xs transition-all"
                      style={{
                        backgroundColor: goalForm.timeframe === tf.value ? colors.accent : colors.bg,
                        color: goalForm.timeframe === tf.value ? colors.onAccent : colors.textSecondary,
                        border: `1px solid ${goalForm.timeframe === tf.value ? colors.accent : colors.border}`
                      }}>
                      {tf.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs block mb-2 font-medium" style={{ color: colors.textMuted }}>Target Date</label>
                <input type="date" value={goalForm.targetDate}
                  onChange={(e) => setGoalForm({ ...goalForm, targetDate: e.target.value })}
                  className="w-full px-4 py-2 rounded-md outline-none"
                  style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }} />
              </div>

              <div className="pt-2">
                <label className="text-xs block mb-3 font-medium" style={{ color: colors.textMuted }}>SMART Framework</label>
                <div className="space-y-3">
                  {SMART_CRITERIA.map(criteria => (
                    <div key={criteria.key}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{criteria.icon}</span>
                        <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>{criteria.label}</span>
                      </div>
                      <textarea value={goalForm[criteria.key]}
                        onChange={(e) => setGoalForm({ ...goalForm, [criteria.key]: e.target.value })}
                        placeholder={criteria.placeholder} rows={2}
                        className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                        style={{ backgroundColor: colors.bg, color: colors.textPrimary, border: `1px solid ${colors.border}` }} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button onClick={handleGoalDelete}
                  className="px-4 py-2 rounded-md" style={{ color: colors.error, border: `1px solid ${colors.error}` }}>
                  <Trash2 size={16} />
                </button>
                <button onClick={() => setShowGoalModal(false)} className="flex-1 py-2 rounded-md"
                  style={{ border: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                  Cancel
                </button>
                <button onClick={handleGoalSave} className="flex-1 py-2 rounded-md font-medium"
                  style={{ backgroundColor: colors.accent, color: colors.onAccent }}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ==================== MOBILE CALENDAR MANAGER MODAL ==================== */}
      {showMobileCalendarManager && createPortal(
        <div 
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 animate-fade-in"
          onClick={() => setShowMobileCalendarManager(false)}
        >
          <div 
            className="w-full md:max-w-md max-h-[85vh] rounded-t-lg md:rounded-lg p-6 overflow-y-auto animate-slide-up"
            style={{ ...getModalStyle(colors) }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>
                Manage Calendars
              </h3>
              <button 
                onClick={() => setShowMobileCalendarManager(false)}
                className="p-2 rounded-full notebook-row-hover"
                style={{ color: colors.textMuted }}
              >
                <X size={20} />
              </button>
            </div>

            {/* My Calendars */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
                  My Calendars
                </span>
                <button 
                  onClick={() => { 
                    setEditingCalendar(null); 
                    setCalendarForm({ name: '', color: CALENDAR_COLORS[0], syncToGoogle: false }); 
                    setShowAddCalendar(true);
                    setShowMobileCalendarManager(false);
                  }}
                  className="p-1.5 rounded-lg"
                  style={{ backgroundColor: colors.bgTertiary, color: colors.accent }}
                >
                  <Plus size={14} />
                </button>
              </div>
              <div className="space-y-2">
                {localCalendars.map(calendar => (
                  <div 
                    key={calendar.id} 
                    className="flex items-center gap-3 p-3 rounded-md"
                    style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                  >
                    <button
                      onClick={() => toggleCalendarVisibility(calendar.id)}
                      className="w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0"
                      style={{ borderColor: calendar.color, backgroundColor: calendar.visible ? calendar.color : 'transparent' }}
                    >
                      {calendar.visible && <Check size={12} color={colors.onAccent} strokeWidth={3} />}
                    </button>
                    <span className="flex-1 text-sm truncate" style={{ color: colors.textPrimary }}>
                      {calendar.name}
                    </span>
                    <button
                      onClick={() => {
                        openCalendarEdit(calendar);
                        setShowMobileCalendarManager(false);
                      }}
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: colors.bgTertiary, color: colors.textMuted }}
                    >
                      <Edit2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Google Calendars */}
            {googleCalendarConnected && googleCalendars.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
                    Google Calendars
                  </span>
                  <button 
                    onClick={syncWithGoogleCalendar} 
                    disabled={syncing}
                    className={`p-1.5 rounded-lg ${syncing ? 'animate-spin' : ''}`}
                    style={{ backgroundColor: colors.bgTertiary, color: colors.textMuted }}
                  >
                    <RefreshCw size={14} />
                  </button>
                </div>
                <div className="space-y-2">
                  {googleCalendars.filter(c => !c.name?.endsWith('- mind.')).map(calendar => {
                    const displayColor = getGoogleCalendarColor(calendar.id);
                    return (
                      <div 
                        key={calendar.id} 
                        className="flex items-center gap-3 p-3 rounded-md"
                        style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                      >
                        <button
                          onClick={() => toggleCalendarVisibility(calendar.id, true)}
                          className="w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0"
                          style={{ borderColor: displayColor, backgroundColor: calendar.visible ? displayColor : 'transparent' }}
                        >
                          {calendar.visible && <Check size={12} color={colors.onAccent} strokeWidth={3} />}
                        </button>
                        <span className="flex-1 text-sm truncate" style={{ color: colors.textPrimary }}>
                          {calendar.name}
                        </span>
                        {calendar.syncError && (
                          <span className="font-mono text-[10px] flex-none" style={{ color: colors.textMuted }} title={calendar.syncError}>not synced</span>
                        )}
                        <button
                          onClick={() => {
                            setEditingGoogleCalendar({ ...calendar, displayColor });
                            setShowMobileCalendarManager(false);
                          }}
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: colors.bgTertiary, color: colors.textMuted }}
                        >
                          <Palette size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tasks & Goals */}
            <div className="mb-6">
              <span className="text-xs font-medium block mb-3" style={{ color: colors.textMuted }}>
                Other Items
              </span>
              <div className="space-y-2">
                {/* Tasks */}
                <div 
                  className="flex items-center gap-3 p-3 rounded-md"
                  style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                >
                  <button
                    onClick={() => setTaskSettings(prev => ({ ...prev, visible: !prev.visible }))}
                    className="w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0"
                    style={{ borderColor: taskSettings.color, backgroundColor: taskSettings.visible ? taskSettings.color : 'transparent' }}
                  >
                    {taskSettings.visible && <Check size={12} color={colors.onAccent} strokeWidth={3} />}
                  </button>
                  <CheckSquare size={16} style={{ color: taskSettings.color }} />
                  <span className="flex-1 text-sm" style={{ color: colors.textPrimary }}>Tasks</span>
                  <button
                    onClick={() => {
                      setEditingSpecialCalendar('tasks');
                      setShowMobileCalendarManager(false);
                    }}
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: colors.bgTertiary, color: colors.textMuted }}
                  >
                    <Palette size={14} />
                  </button>
                </div>

                {/* Goals */}
                <div 
                  className="flex items-center gap-3 p-3 rounded-md"
                  style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                >
                  <button
                    onClick={() => setGoalSettings(prev => ({ ...prev, visible: !prev.visible }))}
                    className="w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0"
                    style={{ borderColor: goalSettings.color, backgroundColor: goalSettings.visible ? goalSettings.color : 'transparent' }}
                  >
                    {goalSettings.visible && <Check size={12} color={colors.onAccent} strokeWidth={3} />}
                  </button>
                  <Target size={16} style={{ color: goalSettings.color }} />
                  <span className="flex-1 text-sm" style={{ color: colors.textPrimary }}>Goals</span>
                  <button
                    onClick={() => {
                      setEditingSpecialCalendar('goals');
                      setShowMobileCalendarManager(false);
                    }}
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: colors.bgTertiary, color: colors.textMuted }}
                  >
                    <Palette size={14} />
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowMobileCalendarManager(false)}
              className="w-full py-3 rounded-md font-medium"
              style={{ backgroundColor: colors.accent, color: colors.onAccent }}
            >
              Done
            </button>
          </div>
        </div>,
        document.body
      )}
      
      {/* Upgrade Limit Modal */}
      <UpgradeLimitModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        itemType="events"
        currentCount={events.length}
        limit={eventLimit}
      />
      </div>
    </div>
  );
};

export default EventsPage;
